import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion'
import { useSiteSettings } from '../hooks/useSiteSettings'
import { ArrowRight, Home as HomeIcon, Building2, Coffee, Layers, Monitor, Gem, Wrench, Clock, Settings, Heart } from 'lucide-react'
import FadeIn from '../components/FadeIn'
import { ExpertiseCarousel } from '../components/ExpertiseCarousel'
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { projects } from '../data/projects'
import residentialCover from '../assets/services/residential-interiors-cover.jpg'
import commercialCover from '../assets/services/commercial-interiors-cover.jpg'
import hospitalityCover from '../assets/services/hospitality-interiors-cover.jpg'
import architectureCover from '../assets/services/architecture-space-planning-cover.jpg'
import visualizationCover from '../assets/services/2d-3d-visualization-cover.jpg'
import developerCover from '../assets/services/developer-solutions-cover.jpg'
import renovationCover from '../assets/services/renovation-makeovers-cover.jpg'
import { testimonials } from '../data/testimonials'
import ConsultationButton from '../components/ConsultationButton'

const heroImg = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1800&q=85'
const philosophyImage = 'https://res.cloudinary.com/tgmyheme/image/upload/v1788063037/nivora/site/our-philosophy-IMG_0171.jpg'


const transformations = [
  {
    id: 1,
    title: 'Living Room Transformation',
    beforeLabel: 'RAW SHELL',
    afterLabel: 'NIVORA DESIGN',
    beforeImg: '/before-1.jpg',
    afterImg: '/after-1.jpg',
    beforeDesc: 'A bare concrete shell with exposed pipes, unfinished walls and no sense of space or identity.',
    afterDesc: 'An opulent living room with ornate carved furniture, crystal chandelier, marble floors and handcrafted wall art.',
  },
  {
    id: 2,
    title: 'Living Area Revival',
    beforeLabel: 'BASIC SPACE',
    afterLabel: 'CURATED INTERIOR',
    beforeImg: '/before-2.jpg',
    afterImg: '/after-2.jpg',
    beforeDesc: 'A plain living area with minimal décor, bare walls and a basic TV unit lacking character.',
    afterDesc: 'A statement TV wall in marble-finish panels, warm ambient lighting, slatted wood accents and a floating media unit.',
  },
  {
    id: 3,
    title: 'Office Reception Design',
    beforeLabel: 'BARE SHELL',
    afterLabel: 'PREMIUM WORKSPACE',
    beforeImg: '/before-3.jpg',
    afterImg: '/after-3.jpg',
    beforeDesc: 'An unfinished commercial space with raw drywall, exposed wiring and construction debris throughout.',
    afterDesc: 'A striking reception with a marble front desk, warm wood-slatted walls, statement chandelier and polished stone flooring.',
  },
  {
    id: 4,
    title: 'Double-Height Hall & Balcony',
    beforeLabel: 'RAW STRUCTURE',
    afterLabel: 'GRAND LIVING',
    beforeImg: '/before-4.jpg',
    afterImg: '/after-4.jpg',
    beforeDesc: 'A bare double-height shell with exposed concrete, raw brick balcony railing and no finishes in place.',
    afterDesc: 'A grand open living hall with a cascading chandelier, glass balcony railing, velvet sofas and warm ambient lighting.',
  },
]

const DEFAULT_INSTAGRAM_POSTS = [
  { image: '/instagram/ig-post-1-bedroom-marble.png',                                                                        url: 'https://www.instagram.com/reel/DNWtK0ht1DO/' },
  { image: 'https://res.cloudinary.com/tgmyheme/image/upload/q_auto:best,f_auto/nivora-instagram/ig-post-2-acetech.png',    url: 'https://www.instagram.com/p/DRAbqc_DSIC/?igsh=cG1kZGN0dGl6dGg5' },
  { image: '/instagram/ig-post-3-sandstone-wall.png',                                                                       url: 'https://www.instagram.com/reel/DPECwNBge6u/' },
  { image: '/instagram/ig-post-4-reception-lounge.png',                                                                     url: 'https://www.instagram.com/reel/DOivw8LjZVS/' },
  { image: '/instagram/ig-post-5-tv-unit.png',                                                                              url: 'https://www.instagram.com/reel/DO3Q44njbsU/' },
  { image: 'https://res.cloudinary.com/tgmyheme/image/upload/q_auto:best,f_auto/nivora-instagram/ig-post-6-bedroom.png',   url: 'https://www.instagram.com/reel/DMlyCnWh6Ct/?igsh=eHd1ZHRuMHoyOGdo' },
]

const stats: { value: string; label: string }[] = []

const services = [
  {
    num: '01',
    title: 'Residential Interiors',
    desc: 'Thoughtfully designed homes that reflect personality, lifestyle and everyday comfort.',
    href: '/services/residential',
    img: residentialCover,
    icon: HomeIcon,
  },
  {
    num: '02',
    title: 'Commercial Interiors',
    desc: 'Functional workspaces, offices and retail environments designed for performance.',
    href: '/services/commercial',
    img: commercialCover,
    icon: Building2,
  },
  {
    num: '03',
    title: 'Hospitality Interiors',
    desc: 'Hotels, cafés and guest experiences crafted to feel memorable and welcoming.',
    href: '/services/hospitality',
    img: hospitalityCover,
    icon: Coffee,
  },
  {
    num: '04',
    title: 'Architecture & Space Planning',
    desc: 'Layouts, planning and built forms that connect aesthetics with purpose.',
    href: '/services/architecture',
    img: architectureCover,
    icon: Layers,
  },
  {
    num: '05',
    title: '2D & 3D Visualization',
    desc: 'Concept drawings, renders and visual development before execution begins.',
    href: '/services/visualization',
    img: visualizationCover,
    icon: Monitor,
  },
  {
    num: '06',
    title: 'Developer Solutions',
    desc: 'Sample flats, amenities and curated experiences that enhance property value.',
    href: '/services/developer',
    img: developerCover,
    icon: Gem,
  },
  {
    num: '07',
    title: 'Renovation & Makeovers',
    desc: 'Transform existing spaces through upgrades, modernization and thoughtful redesign.',
    href: '/services/renovation',
    img: renovationCover,
    icon: Wrench,
  },
]


const DEFAULT_HOME_STATS = [
  { value: 5,  from: 0, suffix: '+', label: 'Years Experience',    duration: 1200 },
  { value: 25, from: 0, suffix: '+', label: 'Projects Completed',  duration: 1800 },
  { value: 50, from: 0, suffix: '+', label: 'Design Concepts',      duration: 1600 },
  { value: 90, from: 0, suffix: '%', label: 'Client Satisfaction', duration: 1400 },
]

function getHomeStatLabel(label: string) {
  return label.trim().toLowerCase() === 'clients served' ? 'Design Concepts' : label
}

function parseStatValue(v: string): { numeric: number; suffix: string } {
  const m = v.match(/^(\d+(?:\.\d+)?)(.*)$/)
  return m ? { numeric: Number(m[1]), suffix: m[2] } : { numeric: 0, suffix: '' }
}

function StatsSection() {
  const { settings } = useSiteSettings()

  const statsData = useMemo(() => {
    if (settings?.homeStats?.length) {
      return settings.homeStats.map(s => {
        const { numeric, suffix } = parseStatValue(s.value)
        return { value: numeric, from: 0, suffix, label: getHomeStatLabel(s.label), duration: 1400 }
      })
    }
    return DEFAULT_HOME_STATS
  }, [settings?.homeStats])

  const [counts, setCounts] = useState(() => statsData.map(() => 0))
  const [shimmer, setShimmer] = useState(() => statsData.map(() => false))

  // Reset counts when settings load and statsData changes
  useEffect(() => {
    setCounts(statsData.map(() => 0))
    setShimmer(statsData.map(() => false))
  }, [statsData])
  const [inView, setInView] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const timersRef = useRef<(() => void)[]>([])

  // IntersectionObserver — toggles inView on every enter/exit
  useEffect(() => {
    const el = cardRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.25 }
    )
    observer.observe(el)
    const rect = el.getBoundingClientRect()
    if (rect.top < window.innerHeight && rect.bottom > 0) setInView(true)
    return () => observer.disconnect()
  }, [])

  // Count-up + shimmer — re-runs every time inView flips to true
  useEffect(() => {
    timersRef.current.forEach(fn => fn())
    timersRef.current = []

    if (!inView) {
      setCounts(statsData.map(() => 0))
      setShimmer(statsData.map(() => false))
      return
    }

    statsData.forEach((stat, i) => {
      const startTime = performance.now()
      let rafId = 0
      const tick = (now: number) => {
        const progress = Math.min((now - startTime) / stat.duration, 1)
        const eased = 1 - Math.pow(1 - progress, 4)
        const val = Math.floor(stat.from + (stat.value - stat.from) * eased)
        setCounts(prev => { const n = [...prev]; n[i] = val; return n })
        if (progress < 1) {
          rafId = requestAnimationFrame(tick)
        } else {
          setCounts(prev => { const n = [...prev]; n[i] = stat.value; return n })
          const shimmerStart = setTimeout(() => {
            setShimmer(prev => { const n = [...prev]; n[i] = true; return n })
            const shimmerOff = setTimeout(() => {
              setShimmer(prev => { const n = [...prev]; n[i] = false; return n })
            }, 900)
            timersRef.current.push(() => clearTimeout(shimmerOff))
          }, 350 + i * 180)
          timersRef.current.push(() => clearTimeout(shimmerStart))
        }
      }
      rafId = requestAnimationFrame(tick)
      timersRef.current.push(() => cancelAnimationFrame(rafId))
    })
  }, [inView])

  return (
    <div className="stats-wrapper" style={{
      position: 'relative',
      zIndex: 10,
      marginTop: 48,
      padding: '0 1.5rem 56px',
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: '#F7F4EF',
    }}>
      <style>{`
        .stat-float-card {
          max-width: 1100px;
          width: 100%;
          background: linear-gradient(135deg, #384F2E 0%, #49613B 50%, #384F2E 100%);
          border-radius: 24px;
          box-shadow: 0 28px 80px rgba(0,0,0,0.30), 0 6px 22px rgba(0,0,0,0.14);
          display: flex;
          align-items: stretch;
          overflow: hidden;
        }
        .stat-float-item {
          flex: 1;
          text-align: center;
          padding: 48px 28px 44px;
          position: relative;
          cursor: default;
          transition: background 0.35s ease;
        }
        .stat-float-item + .stat-float-item {
          border-left: 1px solid rgba(201,169,110,0.13);
        }
        .stat-float-item::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #C9A96E 50%, transparent);
          transition: width 0.5s cubic-bezier(0.16,1,0.3,1);
          border-radius: 2px;
        }
        .stat-float-item:hover {
          background: rgba(255,255,255,0.04);
        }
        .stat-float-item:hover::after {
          width: 52%;
        }
        @keyframes stat-shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .stat-float-num {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.75rem, 4.2vw, 4.25rem);
          font-weight: 300;
          color: #f5f0e8;
          line-height: 1;
          margin-bottom: 16px;
          letter-spacing: -0.02em;
          transition: color 0.25s ease;
          position: relative;
          display: inline-block;
        }
        .stat-float-num.shimmer-active {
          background: linear-gradient(
            105deg,
            #f5f0e8 0%,
            #f5f0e8 35%,
            rgba(255,255,255,0.92) 48%,
            #e8d5b0 52%,
            #f5f0e8 65%,
            #f5f0e8 100%
          );
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: stat-shimmer 0.85s ease-in-out forwards;
        }
        .stat-float-item:hover .stat-float-num {
          color: #ffffff;
        }
        .stat-float-label {
          font-family: 'Jost', sans-serif;
          font-weight: 300;
          font-size: 9px;
          letter-spacing: 4.5px;
          text-transform: uppercase;
          color: rgba(201,169,110,0.60);
          transition: color 0.25s ease;
        }
        .stat-float-item:hover .stat-float-label {
          color: rgba(201,169,110,0.90);
        }
        @media (max-width: 640px) {
          .stat-float-card {
            flex-wrap: wrap;
            border-radius: 18px;
          }
          .stat-float-item {
            flex: 0 0 50%;
            padding: 36px 16px 32px;
          }
          .stat-float-item + .stat-float-item {
            border-left: none;
          }
          .stat-float-item:nth-child(2n) {
            border-left: 1px solid rgba(201,169,110,0.13);
          }
          .stat-float-item:nth-child(n+3) {
            border-top: 1px solid rgba(201,169,110,0.13);
          }
        }
      `}</style>
      <div ref={cardRef} className="stat-float-card">
        {statsData.map((stat, i) => (
          <div key={stat.label} className="stat-float-item">
            <div className={`stat-float-num${shimmer[i] ? ' shimmer-active' : ''}`}>
              {counts[i]}{stat.suffix}
            </div>
            <div className="stat-float-label">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function MobileStatsCarousel() {
  const { settings } = useSiteSettings()
  const slides = settings?.homeStats?.length
    ? settings.homeStats.map(s => ({ value: s.value, label: getHomeStatLabel(s.label) }))
    : [
        { value: '2+',  label: 'Years Experience'    },
        { value: '25+', label: 'Projects Completed'  },
        { value: '50+', label: 'Design Concepts'       },
        { value: '90%', label: 'Client Satisfaction' },
      ]
  const n = slides.length

  const [current, setCurrent]   = useState(0)
  const [slideKey, setSlideKey] = useState(0)
  const [dir, setDir]           = useState<'right' | 'left'>('right')
  const [paused, setPaused]     = useState(false)
  const touchStartX = useRef<number | null>(null)
  const resumeRef   = useRef<ReturnType<typeof setTimeout> | null>(null)

  const advance = useCallback((nextIdx: number, fromDir: 'right' | 'left') => {
    setDir(fromDir)
    setCurrent(nextIdx)
    setSlideKey(k => k + 1)
  }, [])

  // Auto-advance every 2.8 s; resets whenever current or paused changes
  useEffect(() => {
    if (paused) return
    const t = setTimeout(() => advance((current + 1) % n, 'right'), 2800)
    return () => clearTimeout(t)
  }, [current, paused, advance, n])

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    setPaused(true)
    if (resumeRef.current) clearTimeout(resumeRef.current)
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return
    const delta = e.changedTouches[0].clientX - touchStartX.current
    if (Math.abs(delta) > 40) {
      advance(
        delta < 0 ? (current + 1) % n : (current - 1 + n) % n,
        delta < 0 ? 'right' : 'left',
      )
    }
    touchStartX.current = null
    // Resume auto-play 2 s after interaction ends
    resumeRef.current = setTimeout(() => setPaused(false), 2000)
  }

  return (
    <div className="mobile-stats-carousel">
      <style>{`
        /* Hidden on desktop — responsive-mobile.css reveals it on mobile */
        @media (min-width: 768px) { .mobile-stats-carousel { display: none !important; } }

        @keyframes msSlideInRight {
          from { opacity: 0; transform: translateX(36px); }
          to   { opacity: 1; transform: translateX(0);    }
        }
        @keyframes msSlideInLeft {
          from { opacity: 0; transform: translateX(-36px); }
          to   { opacity: 1; transform: translateX(0);     }
        }
        .ms-card-right { animation: msSlideInRight 0.45s cubic-bezier(0.22,1,0.36,1) both; }
        .ms-card-left  { animation: msSlideInLeft  0.45s cubic-bezier(0.22,1,0.36,1) both; }

        .ms-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: rgba(201,169,110,0.28);
          transition: background 0.3s ease, transform 0.3s ease;
          cursor: pointer;
        }
        .ms-dot-active { background: #C9A96E !important; transform: scale(1.4) !important; }
      `}</style>

      <div
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'pan-y', userSelect: 'none' }}
      >
        {/* Stat card — re-keyed on every slide so the animation re-fires */}
        <div
          key={slideKey}
          className={dir === 'right' ? 'ms-card-right' : 'ms-card-left'}
          style={{
            background: 'linear-gradient(135deg, #384F2E 0%, #49613B 50%, #384F2E 100%)',
            borderRadius: 16,
            padding: '44px 24px 40px',
            textAlign: 'center',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 6px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(4rem, 19vw, 5.5rem)',
            fontWeight: 300,
            color: '#f5f0e8',
            lineHeight: 1,
            letterSpacing: '-0.03em',
            marginBottom: 18,
          }}>
            {slides[current].value}
          </div>
          <div style={{
            fontFamily: "'Jost', sans-serif",
            fontWeight: 300,
            fontSize: 10,
            letterSpacing: '3.5px',
            textTransform: 'uppercase',
            color: 'rgba(201,169,110,0.82)',
          }}>
            {slides[current].label}
          </div>
        </div>

        {/* Dot indicators — clickable */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          {slides.map((_, i) => (
            <div
              key={i}
              className={`ms-dot${i === current ? ' ms-dot-active' : ''}`}
              onClick={() => advance(i, i > current ? 'right' : 'left')}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function CompareSlider({
  beforeImg, afterImg, title,
  beforeLabel = 'Before', afterLabel = 'After',
  autoPlayKey = 0,
  onDragChange,
  onUserInteract,
}: {
  beforeImg: string; afterImg: string; title: string;
  beforeLabel?: string; afterLabel?: string;
  autoPlayKey?: number;
  onDragChange?: (isDragging: boolean) => void;
  onUserInteract?: () => void;
}) {
  const [pos, setPos] = useState(50)
  const [transitionMs, setTransitionMs] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const activePointerIdRef = useRef<number | null>(null)
  const isAnimatingRef = useRef(false)
  const manualPausedRef = useRef(false)
  const mountedRef = useRef(true)
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const onDragChangeRef = useRef(onDragChange)
  const onUserInteractRef = useRef(onUserInteract)
  const [handleScale, setHandleScale] = useState(1)
  const [isDragging, setIsDragging] = useState(false)
  const pendingClientXRef = useRef<number | null>(null)
  const positionRafRef = useRef<number | null>(null)
  const [beforeHover, setBeforeHover] = useState(false)
  const [afterHover, setAfterHover] = useState(false)

  useEffect(() => {
    onDragChangeRef.current = onDragChange
    onUserInteractRef.current = onUserInteract
  }, [onDragChange, onUserInteract])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      timeoutsRef.current.forEach(clearTimeout)
      if (positionRafRef.current !== null) {
        cancelAnimationFrame(positionRafRef.current)
      }
    }
  }, [])

  const clearAllTimeouts = () => {
    timeoutsRef.current.forEach(clearTimeout)
    timeoutsRef.current = []
  }

  const cancelAnim = () => {
    clearAllTimeouts()
    isAnimatingRef.current = false
    if (mountedRef.current) setTransitionMs(0)
  }

  const sleep = (ms: number) => new Promise<void>(resolve => {
    const t = setTimeout(resolve, ms)
    timeoutsRef.current.push(t)
  })

  const moveTo = (target: number, duration: number) => new Promise<void>(resolve => {
    if (!mountedRef.current) return resolve()
    setTransitionMs(duration)
    setPos(target)
    const t = setTimeout(resolve, duration)
    timeoutsRef.current.push(t)
  })

  const playReveal = async () => {
    if (manualPausedRef.current || isAnimatingRef.current || draggingRef.current) return
    isAnimatingRef.current = true
    await moveTo(98, 900)
    if (manualPausedRef.current || draggingRef.current || !mountedRef.current) { isAnimatingRef.current = false; return }
    await sleep(600)
    if (manualPausedRef.current || draggingRef.current || !mountedRef.current) { isAnimatingRef.current = false; return }
    await moveTo(2, 900)
    if (manualPausedRef.current || draggingRef.current || !mountedRef.current) { isAnimatingRef.current = false; return }
    await sleep(600)
    if (manualPausedRef.current || draggingRef.current || !mountedRef.current) { isAnimatingRef.current = false; return }
    await moveTo(50, 600)
    if (mountedRef.current) setTransitionMs(0)
    isAnimatingRef.current = false
  }

  // Carousel-triggered play: when autoPlayKey increments, run the reveal
  const prevAutoPlayKeyRef = useRef(autoPlayKey)
  useEffect(() => {
    if (autoPlayKey === prevAutoPlayKeyRef.current) return
    prevAutoPlayKeyRef.current = autoPlayKey
    cancelAnim()
    const t = setTimeout(() => { if (mountedRef.current) playReveal() }, 30)
    timeoutsRef.current.push(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlayKey])

  const applyPosition = (clientX: number) => {
    if (!containerRef.current) return
    setTransitionMs(0)
    const rect = containerRef.current.getBoundingClientRect()
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
    setPos((x / rect.width) * 100)
  }

  const flushPositionUpdate = () => {
    if (positionRafRef.current !== null) {
      cancelAnimationFrame(positionRafRef.current)
      positionRafRef.current = null
    }
    const pendingClientX = pendingClientXRef.current
    pendingClientXRef.current = null
    if (pendingClientX !== null) applyPosition(pendingClientX)
  }

  const queuePositionUpdate = (clientX: number) => {
    pendingClientXRef.current = clientX
    if (positionRafRef.current !== null) return

    positionRafRef.current = requestAnimationFrame(() => {
      positionRafRef.current = null
      const pendingClientX = pendingClientXRef.current
      pendingClientXRef.current = null
      if (draggingRef.current && pendingClientX !== null) {
        applyPosition(pendingClientX)
      }
    })
  }

  const startDrag = (clientX: number) => {
    draggingRef.current = true
    manualPausedRef.current = true
    onUserInteractRef.current?.()
    onDragChangeRef.current?.(true)
    cancelAnim()
    setHandleScale(1.15)
    setIsDragging(true)
    applyPosition(clientX)
  }

  const endDrag = () => {
    pendingClientXRef.current = null
    draggingRef.current = false
    activePointerIdRef.current = null
    onDragChangeRef.current?.(false)
    setHandleScale(1)
    setIsDragging(false)
  }

  // Pointer capture keeps the drag attached to the slider after the finger
  // moves away from the handle or briefly leaves the element. This gives
  // mouse and touch the same continuous, no-jump interaction path.
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    activePointerIdRef.current = e.pointerId
    e.currentTarget.setPointerCapture(e.pointerId)
    e.preventDefault()
    startDrag(e.clientX)
  }
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current || e.pointerId !== activePointerIdRef.current) return
    e.preventDefault()
    queuePositionUpdate(e.clientX)
  }
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerId !== activePointerIdRef.current) return
    flushPositionUpdate()
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    endDrag()
  }
  const onLostPointerCapture = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerId !== activePointerIdRef.current) return
    flushPositionUpdate()
    endDrag()
  }

  const onContainerMouseEnter = () => {
    if (!manualPausedRef.current && !isAnimatingRef.current && !draggingRef.current) playReveal()
  }

  const dividerTransition = transitionMs > 0 ? `left ${transitionMs}ms ease-in-out` : 'none'

  const labelBase: React.CSSProperties = {
    position: 'absolute',
    bottom: 12,
    zIndex: 6,
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 400,
    fontSize: 8,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#C8A56A',
    background: 'rgba(250,248,244,0.92)',
    border: '1px solid rgba(200,165,106,0.55)',
    padding: '4px 9px',
    borderRadius: 100,
    backdropFilter: 'blur(4px)',
    pointerEvents: 'auto',
    cursor: 'default',
    transition: 'opacity 0.2s ease, filter 0.2s ease',
  }

  return (
    <div
      ref={containerRef}
      className="compare-slider-container"
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        cursor: 'ew-resize',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: isDragging ? 'none' : 'pan-y',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onLostPointerCapture={onLostPointerCapture}
      onMouseEnter={onContainerMouseEnter}
    >
      {/* After image — base layer */}
      <img
        src={afterImg}
        alt={`After — ${title}`}
        draggable={false}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
      />

      {/* Before image — clipped to left of divider */}
      <div style={{ position: 'absolute', inset: 0, clipPath: `inset(0 ${100 - pos}% 0 0)`, pointerEvents: 'none',
        transition: transitionMs > 0 ? `clip-path ${transitionMs}ms ease-in-out` : 'none' }}>
        <img
          src={beforeImg}
          alt={`Before — ${title}`}
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>

      {/* Bottom gradient overlay */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 55%, rgba(10,8,6,0.38) 100%)', pointerEvents: 'none', zIndex: 3 }} />

      {/* Divider line */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0,
        left: `${pos}%`, transform: 'translateX(-50%)',
        width: 2,
        background: 'rgba(255,255,255,0.88)',
        pointerEvents: 'none',
        zIndex: 4,
        boxShadow: '0 0 8px rgba(0,0,0,0.25)',
        transition: dividerTransition,
      }} />

      {/* Drag handle */}
      <style>{`
        .cs-handle { width: 46px; height: 46px; }
        @media (max-width: 768px) { .cs-handle { width: 48px; height: 48px; } }
      `}</style>
      <div
        className="cs-handle"
        style={{
          position: 'absolute',
          top: '50%', left: `${pos}%`,
          transform: `translate(-50%, -50%) scale(${handleScale})`,
          borderRadius: '50%',
          background: '#fff',
          border: '2px solid rgba(200,165,106,0.85)',
          boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'auto',
          cursor: 'ew-resize',
          touchAction: isDragging ? 'none' : 'pan-y',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          zIndex: 5,
          transition: dividerTransition ? `left ${transitionMs}ms ease-in-out, transform 120ms ease` : 'transform 120ms ease',
        }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#C8A56A" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 5l-4 7 4 7M16 5l4 7-4 7" />
        </svg>
      </div>

      {/* SLIDE TO COMPARE badge — above handle */}
      <div style={{
        position: 'absolute',
        top: 'calc(50% - 34px)',
        left: `${pos}%`,
        transform: 'translateX(-50%)',
        transition: dividerTransition,
        zIndex: 6,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 400,
        fontSize: 7,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.75)',
        background: 'rgba(0,0,0,0.32)',
        backdropFilter: 'blur(4px)',
        padding: '2px 7px',
        borderRadius: 100,
      }}>
        Slide to Compare
      </div>

      {/* BEFORE label — bottom left; hide when slider is fully right */}
      <span
        style={{
          ...labelBase,
          left: 16,
          opacity: pos > 92 ? 0 : (beforeHover ? 1 : 0.72),
          filter: beforeHover ? 'brightness(1.18)' : 'brightness(1)',
          pointerEvents: pos > 92 ? 'none' : 'auto',
        }}
        onMouseEnter={() => setBeforeHover(true)}
        onMouseLeave={() => setBeforeHover(false)}
      >{beforeLabel}</span>

      {/* AFTER label — bottom right; hide when slider is fully left */}
      <span
        style={{
          ...labelBase,
          right: 16,
          opacity: pos < 8 ? 0 : (afterHover ? 1 : 0.72),
          filter: afterHover ? 'brightness(1.18)' : 'brightness(1)',
          pointerEvents: pos < 8 ? 'none' : 'auto',
        }}
        onMouseEnter={() => setAfterHover(true)}
        onMouseLeave={() => setAfterHover(false)}
      >{afterLabel}</span>
    </div>
  )
}

function TransformationCarousel() {
  const TOTAL = transformations.length
  // Reveal sequence: moveTo(98,900) + sleep(600) + moveTo(2,900) + sleep(600) + moveTo(50,600) = 3600ms
  const REVEAL_MS = 3600
  const STAGGER_MS = 1200
  const PAUSE_AFTER_MS = 1500

  const [cardsPerPage, setCardsPerPage] = useState(2)
  const [currentPage, setCurrentPage] = useState(0)
  const [playKeys, setPlayKeys] = useState<number[]>(() => new Array(TOTAL).fill(0))
  const [sliding, setSliding] = useState(false)

  const cardsPerPageRef = useRef(cardsPerPage)
  const currentPageRef = useRef(currentPage)
  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoPausedRef = useRef(false)
  const draggingSetRef = useRef<Set<number>>(new Set())
  const mountedRef = useRef(true)
  const sectionRef = useRef<HTMLDivElement>(null)
  const hasEnteredRef = useRef(false)

  useEffect(() => { cardsPerPageRef.current = cardsPerPage }, [cardsPerPage])
  useEffect(() => { currentPageRef.current = currentPage }, [currentPage])
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])

  const pageCount = Math.ceil(TOTAL / cardsPerPage)

  const clearAutoTimer = () => {
    if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = null }
  }

  const scheduleAdvance = useCallback((fromPage: number, pageSize: number) => {
    if (autoPausedRef.current) return
    clearAutoTimer()
    const delay = (pageSize - 1) * STAGGER_MS + REVEAL_MS + PAUSE_AFTER_MS
    autoTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return
      if (autoPausedRef.current) return
      if (draggingSetRef.current.size > 0) {
        scheduleAdvance(fromPage, pageSize)
        return
      }
      const cpp = cardsPerPageRef.current
      const pc = Math.ceil(TOTAL / cpp)
      const nextPage = (fromPage + 1) % pc
      setSliding(true)
      setTimeout(() => {
        if (!mountedRef.current || autoPausedRef.current) return
        setCurrentPage(nextPage)
        currentPageRef.current = nextPage
        const start = nextPage * cpp
        const end = Math.min(start + cpp, TOTAL)
        setPlayKeys(prev => {
          const next = [...prev]
          for (let i = start; i < end; i++) next[i]++
          return next
        })
        setTimeout(() => {
          if (mountedRef.current) setSliding(false)
          if (!autoPausedRef.current) scheduleAdvance(nextPage, end - start)
        }, 520)
      }, 0)
    }, delay)
  }, [])

  const goToPage = useCallback((page: number) => {
    autoPausedRef.current = true
    clearAutoTimer()
    const cpp = cardsPerPageRef.current
    const pc = Math.ceil(TOTAL / cpp)
    const target = ((page % pc) + pc) % pc
    draggingSetRef.current.clear()
    setSliding(true)
    setTimeout(() => {
      if (!mountedRef.current) return
      setCurrentPage(target)
      currentPageRef.current = target
      const start = target * cpp
      const end = Math.min(start + cpp, TOTAL)
      setPlayKeys(prev => {
        const next = [...prev]
        for (let i = start; i < end; i++) next[i]++
        return next
      })
      setTimeout(() => {
        if (mountedRef.current) setSliding(false)
        scheduleAdvance(target, end - start)
      }, 520)
    }, 0)
  }, [scheduleAdvance])

  // Responsive cardsPerPage
  useEffect(() => {
    const update = () => {
      const cpp = window.innerWidth < 641 ? 1 : 2
      if (cpp !== cardsPerPageRef.current) {
        cardsPerPageRef.current = cpp
        setCardsPerPage(cpp)
        clearAutoTimer()
        setCurrentPage(0)
        currentPageRef.current = 0
        setPlayKeys(new Array(TOTAL).fill(0))
        draggingSetRef.current.clear()
        setSliding(false)
      }
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  // Trigger page 0 when section enters viewport
  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasEnteredRef.current) {
        hasEnteredRef.current = true
        observer.disconnect()
        const cpp = cardsPerPageRef.current
        const end = Math.min(cpp, TOTAL)
        setPlayKeys(prev => {
          const next = [...prev]
          for (let i = 0; i < end; i++) next[i]++
          return next
        })
        scheduleAdvance(0, end)
      }
    }, { threshold: 0.25 })
    observer.observe(el)
    return () => { observer.disconnect(); clearAutoTimer() }
  }, [scheduleAdvance])

  const handleDragChange = (globalIndex: number, dragging: boolean) => {
    if (dragging) draggingSetRef.current.add(globalIndex)
    else draggingSetRef.current.delete(globalIndex)
  }

  // Responsive: re-trigger page 0 when cardsPerPage resets
  const prevCppRef = useRef(cardsPerPage)
  useEffect(() => {
    if (!hasEnteredRef.current || cardsPerPage === prevCppRef.current) { prevCppRef.current = cardsPerPage; return }
    prevCppRef.current = cardsPerPage
    const end = Math.min(cardsPerPage, TOTAL)
    setPlayKeys(prev => {
      const next = [...prev]
      for (let i = 0; i < end; i++) next[i]++
      return next
    })
    scheduleAdvance(0, end)
  }, [cardsPerPage, scheduleAdvance])

  const STAGGER_OFFSET = 72

  const cardBlock = (globalIndex: number, pageIdx: number = 0) => {
    const t = transformations[globalIndex]
    const verticalShift = cardsPerPage === 2 && pageIdx === 1 ? STAGGER_OFFSET : 0
    return (
      <div key={t.id} className="trf-card" style={{
        flex: cardsPerPage === 1 ? '0 0 100%' : '0 0 calc(50% - 12px)',
        marginTop: verticalShift,
      }}>
        <CompareSlider
          beforeImg={t.beforeImg}
          afterImg={t.afterImg}
          title={t.title}
          beforeLabel={t.beforeLabel}
          afterLabel={t.afterLabel}
          autoPlayKey={playKeys[globalIndex]}
          onDragChange={(d) => handleDragChange(globalIndex, d)}
          onUserInteract={() => {
            autoPausedRef.current = true
            clearAutoTimer()
          }}
        />
        <div className="trf-card-body" style={{ padding: '1.5rem 1.75rem 1.75rem', borderTop: '1px solid #F5F1EA' }}>
          <h3 style={{
            fontFamily: "'Playfair Display', serif", fontWeight: 400,
            fontSize: 'clamp(1.1rem, 1.8vw, 1.4rem)', color: '#262421',
            margin: '0 0 0.9rem', letterSpacing: '-0.01em',
          }}>{t.title}</h3>
          <div className="trf-card-copy" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '1rem 1.5rem' }}>
            <div>
              <p style={{
                fontFamily: "'Montserrat', sans-serif", fontWeight: 400, fontSize: 9,
                letterSpacing: '0.25em', textTransform: 'uppercase',
                color: 'rgba(38,36,33,0.35)', margin: '0 0 0.4rem',
              }}>Before</p>
              <p style={{
                fontFamily: "'Lora', serif", fontWeight: 300, fontSize: 14,
                color: 'rgba(38,36,33,0.55)', lineHeight: 1.75, margin: 0,
              }}>{t.beforeDesc}</p>
            </div>
            <div>
              <p style={{
                fontFamily: "'Montserrat', sans-serif", fontWeight: 400, fontSize: 9,
                letterSpacing: '0.25em', textTransform: 'uppercase',
                color: '#C8A56A', margin: '0 0 0.4rem',
              }}>After</p>
              <p style={{
                fontFamily: "'Lora', serif", fontWeight: 300, fontSize: 14,
                color: 'rgba(38,36,33,0.72)', lineHeight: 1.75, margin: 0,
              }}>{t.afterDesc}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={sectionRef} className="trf-carousel-root" style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px' }}>
      <style>{`
        .trf-card {
          border-radius: 20px;
          background: #fff;
          box-shadow: 0 8px 48px rgba(38,36,33,0.09), 0 2px 12px rgba(38,36,33,0.05);
          overflow: hidden;
          min-width: 0;
        }
        .trf-carousel-viewport { overflow: hidden; }
        .trf-carousel-track {
          display: flex;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
        }
        .trf-carousel-page {
          flex: 0 0 100%;
          display: flex;
          gap: 24px;
          align-items: flex-start;
        }
        .trf-nav-btn {
          width: 46px; height: 46px; border-radius: 50%;
          border: 1px solid #E8DED1; background: #fff; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: border-color 250ms ease, background 250ms ease, opacity 250ms ease;
          flex-shrink: 0;
        }
        .trf-nav-btn:hover { border-color: #C8A56A; background: #FAF8F4; }
        .trf-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: #E8DED1; border: none; cursor: pointer; padding: 0;
          transition: background 300ms ease, transform 300ms ease;
        }
        .trf-dot.active { background: #C8A56A; transform: scale(1.35); }
        @media (max-width: 640px) {
          /* The section already supplies mobile side padding; remove the
             carousel's second inset so the card stays comfortably wide. */
          .trf-carousel-root { padding-left: 0 !important; padding-right: 0 !important; }
          .trf-carousel-page { gap: 0; }
          .trf-card { border-radius: 16px; }
          .trf-card-body { padding: 1.35rem 1.25rem 1.5rem !important; }
          .trf-card-copy { column-gap: 0.85rem !important; }
        }
      `}</style>

      {/* Viewport + track */}
      <div className="trf-carousel-viewport">
        <div
          className="trf-carousel-track"
          style={{ transform: `translateX(-${currentPage * 100}%)` }}
        >
          {Array.from({ length: pageCount }, (_, p) => {
            const start = p * cardsPerPage
            const end = Math.min(start + cardsPerPage, TOTAL)
            const indices = Array.from({ length: end - start }, (_, i) => start + i)
            return (
              <div key={p} className="trf-carousel-page">
                {indices.map((i, pageIdx) => cardBlock(i, pageIdx))}
              </div>
            )
          })}
        </div>
      </div>

      {/* Controls row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginTop: '2.25rem' }}>
        <button
          className="trf-nav-btn"
          onClick={() => goToPage(currentPage - 1)}
          aria-label="Previous page"
          style={{ opacity: sliding ? 0.45 : 1, pointerEvents: sliding ? 'none' : 'auto' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#262421" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M11 6l-6 6 6 6" />
          </svg>
        </button>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {Array.from({ length: pageCount }, (_, p) => (
            <button
              key={p}
              className={`trf-dot${p === currentPage ? ' active' : ''}`}
              onClick={() => goToPage(p)}
              aria-label={`Page ${p + 1}`}
            />
          ))}
        </div>

        <button
          className="trf-nav-btn"
          onClick={() => goToPage(currentPage + 1)}
          aria-label="Next page"
          style={{ opacity: sliding ? 0.45 : 1, pointerEvents: sliding ? 'none' : 'auto' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#262421" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>

      {/* Page counter */}
      <p style={{
        textAlign: 'center', marginTop: '1rem',
        fontFamily: "'Montserrat', sans-serif", fontWeight: 300, fontSize: 11,
        letterSpacing: '0.2em', color: 'rgba(38,36,33,0.3)',
      }}>
        {String(currentPage + 1).padStart(2, '0')} / {String(pageCount).padStart(2, '0')}
      </p>
    </div>
  )
}


function HeroSection({ splashDone }: { splashDone: boolean }) {
  const { settings: heroSettings } = useSiteSettings()
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] })
  const rawY = useTransform(scrollYProgress, [0, 1], ['0%', '22%'])
  const parallaxY = useSpring(rawY, { stiffness: 60, damping: 20 })
  const [heroInView, setHeroInView] = useState(false)

  const heroBg = heroSettings?.homeHero?.backgroundImage || heroImg
  const heroSubheadline = heroSettings?.homeHero?.subheadline || 'We design homes and workspaces that are beautiful, functional, and built for everyday living.'

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => setHeroInView(entry.isIntersecting),
      { threshold: 0.2 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const heroContainerVariants = {
    hidden: {},
    visible: {
      transition: {
        delayChildren: 0,
        staggerChildren: 0.38,
      },
    },
  }
  const heroItemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 1.1, ease: [0.25, 0.1, 0.25, 1] } },
  }
  const heroItalicVariants = {
    hidden: { opacity: 0, y: 24, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 1.1, ease: [0.25, 0.1, 0.25, 1] } },
  }
  const heroTopVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.25, 0.1, 0.25, 1] } },
  }

  return (
    <section
      ref={sectionRef}
      className="hero-section"
      style={{ position: 'relative', height: '100vh', minHeight: 700, overflow: 'hidden', clipPath: 'inset(0)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
    >
      {/* Parallax background */}
      <motion.div
        className="hero-parallax-wrap"
        style={{ y: parallaxY, position: 'absolute', inset: '-12% 0', zIndex: 0 }}
      >
        <img
          src={heroBg}
          alt="NIVORA INTERIORS"
          className="hero-bg-img"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 30%', display: 'block' }}
          loading="eager"
        />
      </motion.div>

      {/* Layered gradient overlay */}
      <div
        className="hero-gradient-overlay"
        style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'linear-gradient(to bottom, rgba(20,32,18,0.35) 0%, rgba(20,32,18,0.55) 40%, rgba(20,32,18,0.78) 75%, rgba(20,32,18,0.92) 100%)',
        }}
      />

      {/* Subtle vignette — edges */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 2,
        background: 'radial-gradient(ellipse at center, transparent 50%, rgba(10,18,9,0.45) 100%)',
        pointerEvents: 'none',
      }} />

      {/* Vertical studio label — left */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: splashDone ? 1 : 0 }}
        transition={{ duration: 0.9, delay: 0 }}
        style={{
          position: 'absolute', left: 36, top: '50%', zIndex: 10,
          transform: 'translateY(-50%) rotate(-90deg)',
          transformOrigin: 'center center',
          fontFamily: "'Cinzel', serif",
          fontSize: 9,
          letterSpacing: '0.35em',
          color: 'rgba(184,150,106,0.45)',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
        className="hidden lg:block"
      >
        Boutique Interior Studio
      </motion.div>

      {/* Vertical year label — right */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: splashDone ? 1 : 0 }}
        transition={{ duration: 0.9, delay: 0 }}
        style={{
          position: 'absolute', right: 36, top: '50%', zIndex: 10,
          transform: 'translateY(-50%) rotate(90deg)',
          transformOrigin: 'center center',
          fontFamily: "'Cinzel', serif",
          fontSize: 9,
          letterSpacing: '0.35em',
          color: 'rgba(184,150,106,0.45)',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
        className="hidden lg:block"
      >
        Mumbai
      </motion.div>

      {/* Centre content — staggered entrance, starts after intro overlay is fully gone */}
      <motion.div
        variants={heroContainerVariants}
        initial="hidden"
        animate={splashDone && heroInView ? 'visible' : 'hidden'}
        style={{ position: 'relative', zIndex: 10, textAlign: 'center', padding: '0 24px', maxWidth: 1040, margin: '0 auto', width: '100%' }}
        className="hero-content-wrap"
      >

        {/* 1. Location indicator */}
        <motion.div
          variants={heroTopVariants}
          className="hero-city-tabs hero-location-tag"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 32 }}
        >
          <svg
            className="hero-location-pin"
            width="12" height="12" viewBox="0 0 24 24"
            fill="none" stroke="#C9A96E" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span
            className="hero-location-text"
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 11,
              letterSpacing: '0.35em',
              textTransform: 'uppercase',
              color: 'rgba(245,240,232,0.85)',
            }}
          >
            Mumbai
          </span>
        </motion.div>

        {/* 2. "Thoughtfully Designed" */}
        <motion.h1
          variants={heroItemVariants}
          className="hero-h1"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 400,
            fontSize: 'clamp(48px, 6.2vw, 88px)',
            lineHeight: 1.05,
            color: '#f5f0e8',
            letterSpacing: '-0.01em',
            marginBottom: 8,
          }}
        >
          Thoughtfully Designed
        </motion.h1>

        {/* 3. "Interiors —" */}
        <motion.h1
          variants={heroItemVariants}
          className="hero-h1"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 400,
            fontSize: 'clamp(48px, 6.2vw, 88px)',
            lineHeight: 1.05,
            color: '#f5f0e8',
            letterSpacing: '-0.01em',
            marginBottom: 10,
          }}
        >
          Interiors —
        </motion.h1>

        {/* 4. "That Feel Effortless" — fade + slide + scale 0.95→1 */}
        <motion.h1
          variants={heroItalicVariants}
          className="hero-h1"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 400,
            fontSize: 'clamp(48px, 6.2vw, 88px)',
            lineHeight: 1.05,
            letterSpacing: '-0.01em',
            marginBottom: 32,
          }}
        >
          <em style={{ color: '#b8966a', fontStyle: 'italic' }}>That Feel Effortless</em>
        </motion.h1>

        {/* 5. Supporting copy */}
        <motion.p
          variants={heroItemVariants}
          className="hero-body"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 300,
            fontSize: 'clamp(15px, 1.6vw, 18px)',
            color: 'rgba(245,240,232,0.58)',
            lineHeight: 1.8,
            maxWidth: 620,
            margin: '0 auto 56px',
          }}
        >
          {heroSubheadline}
        </motion.p>

        {/* 6. CTA buttons */}
        <motion.div
          variants={heroItemVariants}
          className="hero-cta-group"
          style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}
        >
          <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.25 }} className="hero-btn-wrap">
            <ConsultationButton />
          </motion.div>

          <motion.div
            whileHover={{ y: -2, scale: 1.03 }}
            whileTap={{ scale: 1.03 }}
            transition={{ duration: 0.25 }}
            className="hero-btn-wrap"
          >
            <Link
              to="/portfolio"
              className="hero-btn-secondary hero-btn-sweep"
              style={{
                position: 'relative',
                overflow: 'hidden',
                zIndex: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                fontFamily: "'Cinzel', serif",
                fontSize: 10,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                background: 'transparent',
                color: '#D4C0A1',
                padding: '16px 40px',
                border: '1px solid rgba(245,240,232,0.55)',
                textDecoration: 'none',
                transition: 'border-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease',
              }}
            >
              View Projects <ArrowRight size={13} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>

      <style>{`
        .hero-btn-sweep::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #E0C38A 0%, #C8A46A 50%, #A8854F 100%);
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 0.3s ease;
          z-index: -1;
        }
        .hero-btn-sweep:hover,
        .hero-btn-sweep:active {
          border-color: #C8A46A !important;
          color: #2D3E29 !important;
          box-shadow: 0 8px 24px rgba(168,133,79,0.35);
        }
        .hero-btn-sweep:hover::before,
        .hero-btn-sweep:active::before {
          transform: scaleX(1);
        }
      `}</style>

      {/* Stats strip */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0 }}
        style={{
          position: 'absolute',
          bottom: 64,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          display: 'flex',
          gap: 0,
          width: '100%',
          maxWidth: 680,
          padding: '0 24px',
          justifyContent: 'center',
        }}
        className="hero-stats"
      >
        {stats.map((s, i) => (
          <div
            key={s.label}
            style={{
              textAlign: 'center',
              flex: '1 1 0',
              padding: '0 16px',
              borderLeft: i > 0 ? '1px solid rgba(184,150,106,0.2)' : 'none',
            }}
          >
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 28,
              fontWeight: 300,
              color: '#b8966a',
              lineHeight: 1,
              marginBottom: 6,
            }}>
              {s.value}
            </div>
            <div style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 10,
              fontWeight: 300,
              letterSpacing: '0.12em',
              color: 'rgba(245,240,232,0.38)',
              textTransform: 'uppercase',
            }}>
              {s.label}
            </div>
          </div>
        ))}
      </motion.div>

      {/* Scroll indicator — animated line */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0, duration: 0.72 }}
        className="scroll-indicator-bounce"
        style={{
          position: 'absolute',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{
          fontFamily: "'Cinzel', serif",
          fontSize: 8,
          letterSpacing: '0.4em',
          color: 'rgba(184,150,106,0.5)',
          textTransform: 'uppercase',
        }}>
          Scroll
        </span>
        <div style={{ position: 'relative', width: 1, height: 40, background: 'rgba(184,150,106,0.15)', overflow: 'hidden' }}>
          <motion.div
            animate={{ y: ['-100%', '200%'] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.3 }}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '50%', background: 'rgba(184,150,106,0.7)' }}
          />
        </div>
      </motion.div>

      {/* Responsive stats hide on small screens + hero keyframes */}
      <style>{`
        @media (max-width: 640px) {
          .hero-stats { display: none !important; }
        }
        @keyframes scrollBounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50%       { transform: translateX(-50%) translateY(6px); }
        }
        .scroll-indicator-bounce {
          animation: scrollBounce 2s ease-in-out infinite;
        }

        /* ── Hero mobile fixes (below 768px) ── */
        @media (max-width: 767px) {
          /* 1. Compact height + vertically centred content */
          .hero-section {
            height: 100dvh !important;
            min-height: unset !important;
            justify-content: center !important;
            padding-top: 20px !important;
          }

          /* 2. Parallax container — keep slight buffer for the effect */
          .hero-parallax-wrap {
            inset: -8% 0 !important;
          }

          /* 3. Show the interesting mid-section of the building, not the dark tree tops */
          .hero-parallax-wrap img {
            object-position: center 30% !important;
          }

          /* 4. Stronger dark overlay for readability in sunlight */
          .hero-gradient-overlay {
            background: linear-gradient(
              to bottom,
              rgba(10,18,9,0.45) 0%,
              rgba(10,18,9,0.62) 35%,
              rgba(10,18,9,0.82) 68%,
              rgba(10,18,9,0.95) 100%
            ) !important;
          }

          /* 5. Content wrapper — tighter horizontal padding */
          .hero-content-wrap {
            padding: 0 20px !important;
          }

          /* 6. City tabs — compact margin */
          .hero-city-tabs {
            margin-bottom: 20px !important;
          }
          .hero-location-text {
            font-size: 10px !important;
          }
          .hero-location-pin {
            width: 11px !important;
            height: 11px !important;
          }

          /* 7. Heading lines — tighter vertical spacing */
          .hero-h1 {
            margin-bottom: 4px !important;
          }

          /* 8. Body copy — reduce bottom gap */
          .hero-body {
            margin-bottom: 28px !important;
            font-size: 13px !important;
          }

          /* 9. CTA group — vertical stack, full width */
          .hero-cta-group {
            flex-direction: column !important;
            gap: 10px !important;
            align-items: center !important;
            width: 100% !important;
          }

          /* 10. Button wrappers — auto width, centered */
          .hero-btn-wrap {
            width: auto !important;
          }

          /* 11. Primary button — auto width, readable size */
          .hero-btn-primary {
            width: auto !important;
            justify-content: center !important;
            padding: 14px 32px !important;
            font-size: 12px !important;
          }

          /* 12. Secondary button — auto width, readable size */
          .hero-btn-secondary {
            width: auto !important;
            justify-content: center !important;
            padding: 14px 32px !important;
            font-size: 12px !important;
          }
        }
      `}</style>
    </section>
  )
}

const starContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09 } },
}
const starItemVariants = {
  hidden: { opacity: 0, scale: 0.4 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] as [number,number,number,number] } },
}

function TestimonialsCarousel() {
  const [current, setCurrent] = useState(0)
  const [slideKey, setSlideKey] = useState(0)
  const [direction, setDirection] = useState<'next' | 'prev'>('next')
  const [isPaused, setIsPaused] = useState(false)
  const autoScrollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const count = testimonials.length
  const INTERVAL_MS = 5000

  const advance = () => {
    setDirection('next')
    setCurrent(c => (c + 1) % count)
    setSlideKey(k => k + 1)
  }

  const retreat = () => {
    setDirection('prev')
    setCurrent(c => (c - 1 + count) % count)
    setSlideKey(k => k + 1)
  }

  const startAutoScroll = () => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current)
    autoScrollRef.current = setInterval(advance, INTERVAL_MS)
  }

  useEffect(() => {
    startAutoScroll()
    return () => { if (autoScrollRef.current) clearInterval(autoScrollRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const prev = () => { retreat(); startAutoScroll() }
  const next = () => { advance(); startAutoScroll() }

  const touchStartX = useRef<number | null>(null)
  const touchDeltaX = useRef(0)

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchDeltaX.current = 0
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current
  }
  const handleTouchEnd = () => {
    const SWIPE_THRESHOLD = 40
    if (touchDeltaX.current > SWIPE_THRESHOLD) {
      prev()
    } else if (touchDeltaX.current < -SWIPE_THRESHOLD) {
      next()
    }
    touchStartX.current = null
    touchDeltaX.current = 0
  }

  const t = testimonials[current]
  const cardAnim = direction === 'next' ? 'tCardInRight' : 'tCardInLeft'

  return (
    <motion.section
      initial={{ opacity: 0.35 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="testimonials-section"
      style={{ background: '#f5f2ed', padding: '80px 0' }}
    >
      <style>{`
        @keyframes tCardInRight {
          from { opacity: 0; transform: translateX(60px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes tCardInLeft {
          from { opacity: 0; transform: translateX(-60px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes tPanelTextIn {
          from { opacity: 0; transform: translateY(15px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes tDividerGrow {
          from { width: 0; }
          to   { width: 48px; }
        }
        @keyframes tQuoteFade {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes tNameSlide {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes tProgressFill {
          from { width: 0%; }
          to   { width: 100%; }
        }
        .t-nav-btn {
          flex-shrink: 0;
          width: 44px; height: 44px;
          border-radius: 50%;
          border: 1.5px solid rgba(33,41,26,0.25);
          background: transparent;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #21291a;
          transition: border-color 0.2s ease, background 0.2s ease,
                      color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
        }
        .t-nav-btn:hover {
          background: #2e3a24;
          border-color: #2e3a24;
          color: #f5f2ed;
          transform: scale(1.1);
          box-shadow: 0 4px 14px rgba(33,41,26,0.18);
        }
        .t-split-card {
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .t-split-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 48px rgba(33,41,26,0.13);
        }
        .t-read-more {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 400;
          font-size: 10px;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #a18661;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .t-read-more::after {
          content: '';
          position: absolute;
          bottom: -2px; left: 0;
          width: 0; height: 1px;
          background: currentColor;
          transition: width 0.3s ease;
        }
        .t-read-more:hover { color: #21291a; }
        .t-read-more:hover::after { width: 100%; }
        @media (max-width: 768px) {
          /* Keep the two-tone split layout — just shrink it to fit mobile */
          .testimonials-container { padding: 0 16px !important; }
          .t-card-split {
            flex-direction: row !important;
            min-height: unset !important;
            border-radius: 16px !important;
          }

          /* Left panel — dark olive/green, 40% width */
          .t-panel-left {
            display: flex !important;
            flex: 0 0 40% !important;
            max-width: 40% !important;
            padding: 20px 14px !important;
          }
          .t-panel-label { font-size: 7.5px !important; letter-spacing: 0.16em !important; margin-bottom: 10px !important; }
          .t-panel-heading { font-size: 15px !important; line-height: 1.3 !important; margin-bottom: 16px !important; }

          /* Right panel — cream, 60% width */
          .t-panel-right {
            flex: 0 0 60% !important;
            max-width: 60% !important;
            width: auto !important;
            padding: 20px 16px !important;
            border-radius: 0 !important;
          }
          .testimonials-arrow-row {
            width: 100% !important;
            gap: 0 !important;
            position: relative !important;
          }
          .testimonials-arrow-row .t-nav-btn {
            position: absolute !important;
            top: 50% !important;
            transform: translateY(-50%) !important;
            z-index: 5 !important;
            background: rgba(245,242,237,0.9) !important;
            box-shadow: 0 2px 10px rgba(33,41,26,0.18) !important;
          }
          .testimonials-arrow-row .t-nav-btn:first-child { left: -6px !important; }
          .testimonials-arrow-row .t-nav-btn:last-child { right: -6px !important; }
          .t-card-split { width: 100% !important; }

          /* Section padding + heading sizing on mobile */
          .testimonials-section { padding: 40px 0 !important; }
          .testimonials-heading h2 { font-size: 32px !important; text-align: center !important; }
          .testimonials-subtitle { font-size: 14px !important; text-align: center !important; padding: 0 8px !important; }

          /* Review text sizing on mobile */
          .testimonial-quote-text { font-size: 13px !important; line-height: 1.65 !important; margin-bottom: 16px !important; }
          .testimonial-author-name { font-size: 11px !important; }
          .testimonial-author-location { font-size: 10px !important; }

          /* Smaller nav arrows, closer to the card */
          .t-nav-btn { width: 32px !important; height: 32px !important; }

          /* Right panel decorative + meta elements sized down for mobile */
          .t-quote-icon { font-size: 60px !important; top: 2px !important; right: 10px !important; }
          .t-stars-row { margin-bottom: 10px !important; }
          .t-stars-row span { font-size: 12px !important; }
          .t-avatar { width: 30px !important; height: 30px !important; font-size: 9px !important; }

          /* Progress bar full-ish width, centered */
          .testimonial-progress-bar-wrapper { width: 90% !important; margin: 16px auto 0 !important; }
        }
      `}</style>

      <div className="testimonials-container" style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>

        {/* Section heading */}
        <motion.div
          initial={{ opacity: 0.35, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="testimonials-heading"
          style={{ textAlign: 'center', marginBottom: 56 }}
        >
          <p style={{
            fontFamily: "'Montserrat', sans-serif", fontWeight: 400,
            fontSize: 10, letterSpacing: '0.42em', textTransform: 'uppercase',
            color: '#a18661', margin: '0 0 16px',
          }}>Client Stories</p>
          <h2 style={{
            fontFamily: "'Playfair Display', serif", fontWeight: 400,
            fontSize: 'clamp(2.2rem, 5vw, 3.6rem)',
            color: '#21291a', lineHeight: 1.15,
            margin: '0 0 18px', letterSpacing: '-0.01em',
          }}>What Clients Say</h2>
          <p className="testimonials-subtitle" style={{
            fontFamily: "'Lora', serif", fontWeight: 300, fontStyle: 'italic',
            fontSize: 15, lineHeight: 1.75,
            color: 'rgba(33,41,26,0.55)', maxWidth: 500, margin: '0 auto',
          }}>
            Every project is a relationship. These are the words of people who trusted us with their spaces.
          </p>
        </motion.div>

        {/* Arrow + Card + Arrow */}
        <div
          className="testimonials-arrow-row"
          style={{ display: 'flex', alignItems: 'center', gap: 20 }}
          onMouseEnter={() => { setIsPaused(true); if (autoScrollRef.current) clearInterval(autoScrollRef.current) }}
          onMouseLeave={() => { setIsPaused(false); startAutoScroll() }}
        >
          {/* Left arrow */}
          <button className="t-nav-btn" onClick={prev} aria-label="Previous testimonial">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M11 6l-6 6 6 6" />
            </svg>
          </button>

          {/* Two-tone split card */}
          <div
            key={slideKey}
            style={{
              flex: 1,
              display: 'flex',
              border: '1px solid #5f745e',
              borderRadius: 12,
              overflow: 'hidden',
              animation: `${cardAnim} 500ms cubic-bezier(0.22,1,0.36,1) both`,
              minHeight: 280,
            }}
            className="t-card-split"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* LEFT PANEL — dark green */}
            <div
              className="t-panel-left"
              style={{
                flex: '0 0 320px',
                background: '#21291a',
                padding: '44px 40px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              {/* Subtle texture overlay */}
              <div aria-hidden="true" style={{
                position: 'absolute', inset: 0,
                background: 'repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,255,255,0.012) 40px, rgba(255,255,255,0.012) 80px)',
                pointerEvents: 'none',
              }} />

              <p
                key={`label-${slideKey}`}
                className="t-panel-label"
                style={{
                  fontFamily: "'Montserrat', sans-serif", fontWeight: 400,
                  fontSize: 9, letterSpacing: '0.38em', textTransform: 'uppercase',
                  color: '#a18661', margin: '0 0 20px',
                  position: 'relative', zIndex: 1,
                  animation: 'tPanelTextIn 500ms ease-out 200ms both',
                }}
              >The Word on the Street</p>

              <h3
                key={`heading-${slideKey}`}
                className="t-panel-heading"
                style={{
                  fontFamily: "'Playfair Display', serif", fontWeight: 400,
                  fontSize: 'clamp(1.4rem, 2.4vw, 2rem)',
                  color: '#f5f2ed', lineHeight: 1.25,
                  margin: '0 0 28px', letterSpacing: '-0.01em',
                  position: 'relative', zIndex: 1,
                  animation: 'tPanelTextIn 500ms ease-out 280ms both',
                }}
              >Hear what our clients have said about us!</h3>

              {/* Animated gold divider */}
              <div
                key={`divider-${slideKey}`}
                style={{
                  height: 1.5, background: '#a18661',
                  position: 'relative', zIndex: 1,
                  animation: 'tDividerGrow 600ms ease-out 360ms both',
                }}
              />
            </div>

            {/* RIGHT PANEL — light cream */}
            <div
              className="t-panel-right"
              style={{
                flex: 1,
                background: '#ffffff',
                padding: '44px 44px 40px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Decorative closing quote — animated on slide change */}
              <motion.span
                key={`quoteicon-${slideKey}`}
                className="t-quote-icon"
                aria-hidden="true"
                initial={{ opacity: 0.1, scale: 0.8, rotate: -10 }}
                animate={{ opacity: 0.25, scale: 1, rotate: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  position: 'absolute', top: 8, right: 24,
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 110, lineHeight: 1,
                  color: '#a18661',
                  pointerEvents: 'none', userSelect: 'none',
                  display: 'block',
                }}
              >"</motion.span>

              {/* Stars — staggered fill on each slide change */}
              <motion.div
                key={`stars-${slideKey}`}
                className="t-stars-row"
                variants={starContainerVariants}
                initial="hidden"
                animate="visible"
                style={{ display: 'flex', gap: 4, marginBottom: 18, position: 'relative', zIndex: 1 }}
              >
                {Array.from({ length: t.stars }).map((_, i) => (
                  <motion.span
                    key={i}
                    variants={starItemVariants}
                    style={{ fontSize: 16, color: '#a18661', lineHeight: 1, display: 'block' }}
                  >★</motion.span>
                ))}
              </motion.div>

              <p
                key={`quote-${slideKey}`}
                className="testimonial-quote-text"
                style={{
                  fontFamily: "'Lora', serif", fontStyle: 'italic',
                  fontSize: 16, lineHeight: 1.8,
                  color: '#2c2c2c', margin: '0 0 28px',
                  position: 'relative', zIndex: 1,
                  animation: 'tQuoteFade 500ms ease-out 320ms both',
                }}
              >"{t.text}"</p>

              {/* Thin gold divider before name */}
              <div style={{ width: 36, height: 1, background: '#a18661', marginBottom: 16, flexShrink: 0 }} />

              {/* Client info row — avatar + name/location */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Avatar circle — animates in on each slide change */}
                <motion.div
                  key={`avatar-${slideKey}`}
                  className="t-avatar"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.35, delay: 0.44, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    width: 38, height: 38,
                    borderRadius: '50%',
                    background: 'rgba(161,134,97,0.15)',
                    border: '1.5px solid rgba(161,134,97,0.5)',
                    color: '#7a6142',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 700, fontSize: 11,
                    flexShrink: 0,
                    letterSpacing: '0.04em',
                  }}
                >
                  {t.initials}
                </motion.div>

                <div>
                  <p
                    key={`name-${slideKey}`}
                    className="testimonial-author-name"
                    style={{
                      fontFamily: "'Montserrat', sans-serif", fontWeight: 500,
                      fontSize: 12, letterSpacing: '0.14em',
                      textTransform: 'uppercase', color: '#21291a', margin: '0 0 5px',
                      animation: 'tNameSlide 400ms ease-out 440ms both',
                    }}
                  >{t.name}</p>
                  <p
                    key={`loc-${slideKey}`}
                    className="testimonial-author-location"
                    style={{
                      fontFamily: "'Montserrat', sans-serif", fontWeight: 300,
                      fontSize: 10, letterSpacing: '0.1em',
                      textTransform: 'uppercase', color: '#a18661', margin: 0,
                      animation: 'tNameSlide 400ms ease-out 520ms both',
                    }}
                  >{t.location}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right arrow */}
          <button className="t-nav-btn" onClick={next} aria-label="Next testimonial">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>

        {/* Progress bar — syncs with auto-scroll interval, resets on each slide change */}
        <div className="testimonial-progress-bar-wrapper" style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
          <div style={{
            width: '100%',
            height: 3, background: '#e0d9cf',
            borderRadius: 3, overflow: 'hidden',
          }}>
            <div
              key={`bar-${slideKey}`}
              style={{
                height: '100%',
                background: 'linear-gradient(90deg, #a18661, #c8a97e)',
                borderRadius: 3,
                animation: `tProgressFill ${INTERVAL_MS}ms linear forwards`,
                animationPlayState: isPaused ? 'paused' : 'running',
              }}
            />
          </div>
        </div>

        {/* Read all link — fades in last, after card content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          style={{ textAlign: 'center', marginTop: 28 }}
        >
          <Link to="/testimonials" className="t-read-more">
            Read All Client Stories →
          </Link>
        </motion.div>

      </div>
    </motion.section>
  )
}

// ─── Mobile Services Section ─────────────────────────────────────────────────
function MobileServicesSection() {
  const [inView, setInView] = useState(false)
  const [animKey, setAnimKey] = useState(0)
  const [pressedIdx, setPressedIdx] = useState<number | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimKey(k => k + 1)
          setInView(true)
        } else {
          setInView(false)
        }
      },
      { threshold: 0.05 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const gridSvcs = services.slice(0, 4)

  // Brief press flash + description reveal, then navigate
  const handleTap = (idx: number) => {
    setPressedIdx(idx)
    setTimeout(() => {
      setPressedIdx(null)
      navigate('/services')
    }, 350)
  }

  return (
    <div ref={rootRef} className="mob-svc-root">
      <style>{`
        .mob-svc-root { display: none; }

        @media (max-width: 767px) {
          /* Hide desktop layout */
          .hsvc-desktop-header,
          .hsvc-desktop-cards { display: none !important; }

          .mob-svc-root { display: block; }

          /* ── Heading ── */
          .mob-svc-heading {
            text-align: center;
            margin-bottom: 1.75rem;
            opacity: 0;
            transform: translateY(-28px);
            transition: opacity 0.55s ease-out 0s, transform 0.55s ease-out 0s;
          }
          .mob-svc-heading.msv-in {
            opacity: 1;
            transform: translateY(0);
          }

          /* ── Press feedback (scale + brief description reveal, no expand) ── */
          .mob-svc-gcard.msv-pressed {
            animation-name: none !important;
            transform: scale(1.02) !important;
            filter: brightness(1.14);
            transition: transform 120ms ease, filter 120ms ease;
          }

          /* ── 2x2 grid — 4 cards, equal size ── */
          .mob-svc-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }

          /* ── Grid card ── */
          .mob-svc-gcard {
            position: relative;
            height: 190px;
            border-radius: 16px;
            overflow: hidden;
            cursor: pointer;
            opacity: 0;
            transform: translateY(24px);
          }
          .mob-svc-gcard.msv-card-in {
            animation: msvCardIn 0.45s cubic-bezier(0.22,1,0.36,1) forwards;
          }

          /* ── Description — hidden by default, briefly revealed on press ── */
          .mob-svc-detail {
            opacity: 0;
            max-height: 0;
            overflow: hidden;
            transition: opacity 150ms ease, max-height 150ms ease;
            margin-top: 4px;
          }
          .mob-svc-gcard.msv-pressed .mob-svc-detail {
            opacity: 1;
            max-height: 70px;
          }
          .mob-svc-desc {
            font-family: 'Jost', sans-serif;
            font-weight: 300;
            font-size: 10.5px;
            line-height: 1.4;
            color: rgba(245,240,232,0.85);
            margin: 0 0 4px 0;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .mob-svc-explore {
            display: inline-flex; align-items: center; gap: 4px;
            font-family: 'Jost', sans-serif; font-weight: 300;
            font-size: 9px; letter-spacing: 0.18em;
            text-transform: uppercase; color: #C8A56A;
          }

          /* ── Image ── */
          .mob-svc-img {
            width: 100%; height: 100%; object-fit: cover; display: block;
          }

          /* ── Dark overlay ── */
          .mob-svc-overlay {
            position: absolute; inset: 0; pointer-events: none;
            background: linear-gradient(
              to bottom,
              rgba(0,0,0,0.06) 0%,
              rgba(0,0,0,0.52) 52%,
              rgba(0,0,0,0.90) 100%
            );
          }

          /* ── Bottom-left: icon + title — always visible, never moves ── */
          .mob-svc-bottom {
            position: absolute;
            left: 14px; bottom: 14px; right: 14px;
            z-index: 3;
          }
          .mob-svc-featured .mob-svc-bottom { left: 16px; bottom: 16px; right: 16px; }

          .mob-svc-icon-wrap {
            width: 28px; height: 28px; border-radius: 7px;
            background: rgba(200,165,106,0.18);
            display: flex; align-items: center; justify-content: center;
            margin-bottom: 5px;
          }
          .mob-svc-featured .mob-svc-icon-wrap {
            width: 36px; height: 36px; border-radius: 10px;
          }

          .mob-svc-title {
            font-family: 'Cormorant Garamond', serif;
            font-weight: 300;
            font-size: 0.875rem;
            color: #f5f0e8;
            line-height: 1.2;
            margin: 0;
          }
          .mob-svc-featured .mob-svc-title { font-size: 1.2rem; }

          /* ── Shimmer sweep ── */
          .mob-svc-shimmer {
            position: absolute; inset: 0; z-index: 5; pointer-events: none;
            overflow: hidden; border-radius: 16px;
          }
          .mob-svc-shimmer::after {
            content: '';
            position: absolute; top: 0; left: -100%;
            width: 55%; height: 100%;
            background: linear-gradient(
              105deg,
              transparent 20%,
              rgba(255,255,255,0.11) 50%,
              transparent 80%
            );
            animation-fill-mode: forwards;
            animation-timing-function: ease-out;
            animation-duration: 0.75s;
            animation-delay: var(--msv-shimmer-delay, 0.2s);
          }
          .mob-svc-shimmer.msv-shimmer-run::after {
            animation-name: msvShimmer;
          }

          /* ── Keyframes ── */
          @keyframes msvFeatIn {
            from { opacity: 0; transform: scale(0.9); }
            to   { opacity: 1; transform: scale(1); }
          }
          @keyframes msvCardIn {
            from { opacity: 0; transform: translateY(24px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes msvShimmer {
            0%   { left: -100%; }
            100% { left: 200%; }
          }
        }
      `}</style>

      {/* Section heading — slides down from top */}
      <div className={`mob-svc-heading${inView ? ' msv-in' : ''}`}>
        <p style={{
          fontFamily: "'Jost', sans-serif", fontWeight: 300,
          fontSize: 10, letterSpacing: '0.45em',
          textTransform: 'uppercase', color: '#C8A56A', marginBottom: '0.75rem',
        }}>Our Services</p>
        <h2 style={{
          fontFamily: "'Playfair Display', serif", fontWeight: 400,
          fontSize: 'clamp(1.75rem, 7vw, 2.5rem)',
          color: '#262421', lineHeight: 1.1, marginBottom: '0.75rem',
          letterSpacing: '-0.01em',
        }}>Spaces Designed Across<br />Every Experience</h2>
        <p style={{
          fontFamily: "'Jost', sans-serif", fontWeight: 300,
          fontSize: 13, color: 'rgba(38,36,33,0.5)', lineHeight: 1.8, margin: 0,
        }}>
          From private residences to large-scale environments, we design spaces that balance beauty, function and identity.
        </p>
      </div>

      {/* 2x2 grid — 4 cards, equal size */}
      <div className="mob-svc-grid">
        {gridSvcs.map((s, i) => {
          const Icon = s.icon
          const cardDelay = i * 0.1
          const shimDelay = cardDelay + 0.18
          return (
            <div
              key={`mgc-${animKey}-${i}`}
              className={`mob-svc-gcard${inView ? ' msv-card-in' : ''}${pressedIdx === i ? ' msv-pressed' : ''}`}
              style={{ animationDelay: inView ? `${cardDelay}s` : '0s' }}
              onClick={() => handleTap(i)}
            >
              <img src={s.img} alt={s.title} className="mob-svc-img" loading="lazy" />
              <div className="mob-svc-overlay" />
              <div
                className={`mob-svc-shimmer${inView ? ' msv-shimmer-run' : ''}`}
                key={`mgshim-${animKey}-${i}`}
                style={{ '--msv-shimmer-delay': `${shimDelay}s` } as React.CSSProperties}
              />
              <div className="mob-svc-bottom">
                <div className="mob-svc-icon-wrap">
                  <Icon size={13} color="#C8A56A" strokeWidth={1.4} />
                </div>
                <h3 className="mob-svc-title">{s.title}</h3>
                <div className="mob-svc-detail">
                  <p className="mob-svc-desc">{s.desc}</p>
                  <span className="mob-svc-explore">
                    Explore <ArrowRight size={9} strokeWidth={1.5} />
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const DEFAULT_SERVICE_CARDS = [
  { img: 'https://res.cloudinary.com/tgmyheme/image/upload/v1787286193/nivora/home/our-expertise/living-room-design.jpg', title: 'Living Room Design', desc: 'Sophisticated and welcoming living spaces designed for comfort, conversation, and everyday luxury.' },
  { img: 'https://res.cloudinary.com/tgmyheme/image/upload/v1787286195/nivora/home/our-expertise/modular-kitchen.jpg', title: 'Modular Kitchen', desc: 'Smart, elegant kitchens with seamless storage solutions, premium finishes, and functional layouts.' },
  { img: 'https://res.cloudinary.com/tgmyheme/image/upload/v1787286197/nivora/home/our-expertise/bedroom-interiors.jpg', title: 'Bedroom Interiors', desc: 'Calm and luxurious retreats crafted with warm textures, custom furniture, and ambient lighting.' },
  { img: 'https://res.cloudinary.com/tgmyheme/image/upload/v1787286199/nivora/home/our-expertise/bathroom-interiors.jpg', title: 'Bathroom Interiors', desc: 'Serene, spa-inspired bathrooms designed with premium fittings, elegant tiling, and thoughtful layouts for everyday indulgence.' },
]

function InstagramFeed({ posts }: { posts: Array<{ image: string; url: string }> }) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [vis, setVis] = useState(false)

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const io = new IntersectionObserver(([e]) => setVis(e.isIntersecting), { threshold: 0.1 })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const words = ['Follow', 'Our', 'Journey']

  return (
    <div ref={sectionRef}>
      <style>{`
        @keyframes ig2-word-drop {
          from { transform: translateY(-110%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
        @keyframes ig2-fade-up {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes ig2-thumb-in {
          from { opacity: 0; transform: scale(0.85); }
          to   { opacity: 1; transform: scale(1);    }
        }
        @keyframes ig2-shimmer {
          0%   { transform: translateX(-120%) skewX(-12deg); }
          100% { transform: translateX(220%)  skewX(-12deg); }
        }
        @keyframes ig2-underline {
          from { width: 0;    }
          to   { width: 100%; }
        }

        .ig2-grid {
          display: flex;
          width: 100%;
          gap: 0;
          padding: 0;
          margin: 0;
        }
        @media (max-width: 768px) {
          .ig2-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3px; padding: 0; }
        }

        .ig2-thumb {
          flex: 1;
          display: block;
          position: relative;
          overflow: hidden;
          height: 300px;
          cursor: pointer;
          opacity: 0;
          border: 2px solid transparent;
          box-sizing: border-box;
          border-radius: 0;
          transition: border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
        }
        @media (max-width: 768px) {
          .ig2-thumb { flex: none; height: auto; aspect-ratio: 1 / 1; }
        }
        .ig2-thumb.ig2-in {
          animation: ig2-thumb-in 0.5s cubic-bezier(0.22,1,0.36,1) forwards;
          animation-delay: var(--thumb-delay, 0s);
        }
        .ig2-thumb::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.22) 50%, transparent 65%);
          transform: translateX(-120%) skewX(-12deg);
          pointer-events: none;
          z-index: 3;
        }
        .ig2-thumb.ig2-shimmer::after {
          animation: ig2-shimmer 0.75s ease forwards;
          animation-delay: var(--shimmer-delay, 0.5s);
        }
        .ig2-thumb:hover {
          transform: scale(1.02);
          border-color: rgba(200,165,106,0.75);
          box-shadow: 0 4px 24px rgba(0,0,0,0.18);
          z-index: 2;
        }
        .ig2-thumb img {
          width: 100%; height: 100%;
          object-fit: cover; display: block;
          transition: filter 0.3s ease;
          pointer-events: none;
        }
        .ig2-overlay {
          position: absolute; inset: 0; z-index: 2;
          background: rgba(0,0,0,0);
          display: flex; align-items: center; justify-content: center;
          transition: background 0.3s ease;
        }
        .ig2-overlay svg { opacity: 0; transition: opacity 0.3s ease; }
        .ig2-thumb:hover .ig2-overlay { background: rgba(0,0,0,0.3); }
        .ig2-thumb:hover .ig2-overlay svg { opacity: 1; }

        .ig2-word-wrap { display: inline-block; overflow: hidden; vertical-align: bottom; }
        .ig2-word { display: inline-block; transform: translateY(-110%); opacity: 0; }
        .ig2-word.ig2-in {
          animation: ig2-word-drop 0.6s cubic-bezier(0.22,1,0.36,1) forwards;
          animation-delay: var(--word-delay, 0s);
        }

        .ig2-sub { opacity: 0; }
        .ig2-sub.ig2-in { animation: ig2-fade-up 0.5s ease forwards; animation-delay: var(--sub-delay, 0s); }

        .ig2-cta-wrap { opacity: 0; }
        .ig2-cta-wrap.ig2-in { animation: ig2-fade-up 0.5s ease forwards; animation-delay: var(--cta-delay, 0s); }

        .ig2-cta {
          position: relative;
          display: inline-flex; align-items: center; gap: 8px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 400; font-size: 11px;
          letter-spacing: 0.22em; text-transform: uppercase;
          color: #a18661; text-decoration: none;
        }
        .ig2-cta::after {
          content: '';
          position: absolute; bottom: -2px; left: 0;
          width: 0; height: 1px; background: #a18661;
        }
        .ig2-cta-wrap.ig2-in .ig2-cta::after {
          animation: ig2-underline 0.5s ease forwards;
          animation-delay: var(--cta-ul-delay, 0s);
        }
        .ig2-cta-arrow { display: inline-flex; align-items: center; transition: transform 0.25s ease; }
        .ig2-cta:hover .ig2-cta-arrow { transform: translateX(4px); }
      `}</style>

      {/* Heading */}
      <div className="text-center" style={{ maxWidth: 1200, margin: '0 auto 24px', padding: '0 24px' }}>
        <a
          href="https://www.instagram.com/nivora.interiors"
          target="_blank" rel="noopener noreferrer"
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 400, fontSize: 11,
            letterSpacing: '0.15em', textTransform: 'uppercase',
            color: '#a18661', marginBottom: 14,
            display: 'inline-block', textDecoration: 'none',
          }}
        >@NivoraInteriors</a>

        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontWeight: 400,
          fontSize: 'clamp(1.8rem, 4vw, 3rem)',
          color: '#262421', marginBottom: 10, lineHeight: 1.1,
        }}>
          {words.map((word, wi) => (
            <span key={word} className="ig2-word-wrap" style={{ marginRight: wi < words.length - 1 ? '0.28em' : 0 }}>
              <span
                className={`ig2-word${vis ? ' ig2-in' : ''}`}
                style={{ '--word-delay': `${wi * 0.15}s` } as React.CSSProperties}
              >{word}</span>
            </span>
          ))}
        </h2>

        <p
          className={`ig2-sub${vis ? ' ig2-in' : ''}`}
          style={{
            fontFamily: "'Lora', serif", fontWeight: 300, fontSize: 14,
            color: 'rgba(38,36,33,0.5)', margin: 0,
            '--sub-delay': `${words.length * 0.15 + 0.1}s`,
          } as React.CSSProperties}
        >Daily design inspiration and behind-the-scenes site visits</p>
      </div>

      {/* Grid */}
      <div className="ig2-grid">
        {posts.map((post, i) => (
          <a
            key={i}
            href={post.url}
            target="_blank" rel="noopener noreferrer"
            className={`ig2-thumb${vis ? ' ig2-in ig2-shimmer' : ''}`}
            style={{
              '--thumb-delay':   `${i * 0.1}s`,
              '--shimmer-delay': `${0.5 + i * 0.1}s`,
            } as React.CSSProperties}
          >
            <img src={post.image} alt={`@NivoraInteriors post ${i + 1}`} loading="lazy" />
            <div className="ig2-overlay">
              <svg width="24" height="24" fill="white" viewBox="0 0 24 24"
                style={{ filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.4))' }}>
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </div>
          </a>
        ))}
      </div>

      {/* CTA */}
      <div
        className={`ig2-cta-wrap${vis ? ' ig2-in' : ''}`}
        style={{
          textAlign: 'center', maxWidth: 1200, marginTop: 24, marginLeft: 'auto', marginRight: 'auto', padding: '0 24px',
          '--cta-delay':    `${posts.length * 0.1 + 0.35}s`,
          '--cta-ul-delay': `${posts.length * 0.1 + 0.55}s`,
        } as React.CSSProperties}
      >
        <a
          href="https://www.instagram.com/nivora.interiors/"
          target="_blank" rel="noopener noreferrer"
          className="ig2-cta"
        >
          SEE MORE ON INSTAGRAM <span className="ig2-cta-arrow"><ArrowRight size={12} /></span>
        </a>
      </div>
    </div>
  )
}

export default function Home({ splashDone }: { splashDone: boolean }) {
  const { settings } = useSiteSettings()
  const serviceCards = settings?.serviceCards?.length ? settings.serviceCards : DEFAULT_SERVICE_CARDS
  const instagramPosts = settings?.instagramPosts?.length ? settings.instagramPosts : DEFAULT_INSTAGRAM_POSTS
  const philosophyImageUrl = settings?.homePhilosophyImage || philosophyImage
  const location = useLocation()
  const [animKey, setAnimKey] = useState(0)
  const philosophySectionRef = useRef<HTMLElement>(null)
  const philosophyImgRef = useRef<HTMLImageElement>(null)
  const quoteCardRef = useRef<HTMLDivElement>(null)
  const [philosophyInView, setPhilosophyInView] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()                                    // set correct value after mount
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  /* Left column — fade in + slide in from the left, staggered 0.2s apart */
  const leftEl = (delay: number): React.CSSProperties => ({
    opacity: philosophyInView ? 1 : 0,
    transform: philosophyInView ? 'translateX(0)' : 'translateX(-40px)',
    transition: `opacity 600ms ease-out ${delay}ms, transform 600ms ease-out ${delay}ms`,
  })

  /* Timeless / Functional / Personal icons — fade in from bottom, staggered */
  const iconEl = (index: number): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    opacity: philosophyInView ? 1 : 0,
    transform: philosophyInView ? 'translateY(0)' : 'translateY(14px)',
    transition: `opacity 500ms ease-out ${700 + index * 150}ms, transform 500ms ease-out ${700 + index * 150}ms`,
  })

  useEffect(() => {
    const section = philosophySectionRef.current
    if (!section) return
    const observer = new IntersectionObserver(
      ([entry]) => { setPhilosophyInView(entry.isIntersecting) },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    )
    observer.observe(section)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const section = philosophySectionRef.current
    const img = philosophyImgRef.current
    if (!img || !section) return
    const handleScroll = () => {
      // Disable parallax on mobile — the transform crops the image inside
      // the overflow:hidden container, showing only the ceiling
      if (window.innerWidth < 768) {
        img.style.transform = 'none'
        return
      }
      const rect = section.getBoundingClientRect()
      const progress = -rect.top / window.innerHeight
      img.style.transform = `translateY(${progress * 30}px)`
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (location.pathname === '/') {
      setAnimKey(k => k + 1)
      window.scrollTo(0, 0)
    }
  }, [location.key])

  return (
    <div style={{ backgroundColor: '#2D3E29' }}>
      {/* Hero */}
      <HeroSection key={animKey} splashDone={splashDone} />

      {/* Philosophy */}
      <section ref={philosophySectionRef} className={`philosophy-section${philosophyInView ? ' philosophy-in-view' : ''}`} style={{ backgroundColor: '#f7f4ef', padding: '60px 1.5rem 60px' }}>
        <div className="philosophy-flex" style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: '4rem', alignItems: 'center', flexWrap: 'wrap' }}>

          {/* Left — text block (55%) */}
          <div
            className="philosophy-text-block"
            style={{
              flex: '0 0 55%',
              minWidth: 280,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A96E' fill-opacity='0.04'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E")`,
              backgroundRepeat: 'repeat',
            }}
          >

            {/* Label with flanking rules */}
            <div className="philosophy-mobile-fade philosophy-label-row" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: '2.5rem', ...leftEl(0) }}>
              <div className="philosophy-label-rule" style={{ height: '0.5px', backgroundColor: '#b8966a', width: 60 }} />
              <span style={{
                fontFamily: "'Jost', sans-serif",
                fontWeight: 300,
                fontSize: 10,
                letterSpacing: '0.35em',
                color: '#b8966a',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}>Our Philosophy</span>
              <div className="philosophy-label-rule" style={{ height: '0.5px', backgroundColor: '#b8966a', width: 60 }} />
            </div>

            {/* Quote */}
            <p className="philosophy-mobile-fade" style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(1.8rem, 3.2vw, 2.75rem)',
              fontWeight: 300,
              lineHeight: 1.25,
              color: '#3b2f1e',
              marginBottom: '1.75rem',
              ...leftEl(200),
            }}>
              "Design is not just seen —{' '}
              <em style={{ color: '#8b6914', fontStyle: 'italic' }}>it is experienced.</em>"
            </p>

            {/* Body */}
            <p className="philosophy-mobile-fade" style={{
              fontFamily: "'Jost', sans-serif",
              fontWeight: 300,
              fontSize: '0.9375rem',
              lineHeight: 1.85,
              color: '#6b5240',
              marginBottom: '2.5rem',
              ...leftEl(400),
            }}>
              At NIVORA, every project begins with understanding — how you move through a space, what you need from it, and what makes it feel unmistakably yours. We work with refined materials, considered proportions, and timeless palettes to create interiors that hold their beauty for years, not seasons.
            </p>

            {/* Divider + brand values */}
            <div className="philosophy-mobile-fade" style={{ ...leftEl(600) }}>
              {/* Animated divider draw */}
              <div style={{ overflow: 'hidden', marginBottom: '1.5rem' }}>
                <div style={{
                  height: '0.5px',
                  backgroundColor: '#c9b99a',
                  width: philosophyInView ? '100%' : '0%',
                  transition: 'width 800ms ease-out 600ms',
                }} />
              </div>
              <div style={{
                fontFamily: "'Jost', sans-serif",
                fontWeight: 300,
                fontSize: 11,
                letterSpacing: '3px',
                color: '#C9A96E',
                textAlign: 'center',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                margin: 0,
              }}>
                <span style={iconEl(0)}>
                  <Clock size={14} color="#C9A96E" strokeWidth={1.5} />
                  Timeless
                </span>
                <span style={{ fontSize: 8, opacity: 0.5 }}>◆</span>
                <span style={iconEl(1)}>
                  <Settings size={14} color="#C9A96E" strokeWidth={1.5} />
                  Functional
                </span>
                <span style={{ fontSize: 8, opacity: 0.5 }}>◆</span>
                <span style={iconEl(2)}>
                  <Heart size={14} color="#C9A96E" strokeWidth={1.5} />
                  Personal
                </span>
              </div>
            </div>

            {/* Discover Our Story link — fades in last, underline draws itself */}
            <div className="philosophy-mobile-fade" style={{
              marginTop: 28,
              opacity: philosophyInView ? 1 : 0,
              transform: philosophyInView ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'opacity 600ms ease-out 1150ms, transform 600ms ease-out 1150ms',
            }}>
              <Link
                to="/about"
                style={{
                  position: 'relative',
                  fontFamily: "'Cormorant Garamond', serif",
                  fontStyle: 'italic',
                  fontWeight: 400,
                  fontSize: '18px',
                  color: '#C9A96E',
                  textDecoration: 'none',
                  letterSpacing: '0.06em',
                  display: 'inline-block',
                  paddingBottom: 3,
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#A07840' }}
                onMouseLeave={e => { e.currentTarget.style.color = '#C9A96E' }}
              >
                Discover Our Story →
                <span style={{
                  position: 'absolute',
                  left: 0,
                  bottom: 0,
                  height: 1,
                  backgroundColor: 'currentColor',
                  width: philosophyInView ? '100%' : '0%',
                  transition: 'width 700ms ease-out 1550ms, background-color 0.2s ease',
                }} />
              </Link>
            </div>

          </div>

          {/* Right — editorial photo (45%) */}
          <div
            className="philosophy-image-col"
            style={{
              flex: 1,
              minWidth: 240,
              alignSelf: 'stretch',
              opacity: philosophyInView ? 1 : 0,
              transform: philosophyInView ? 'translateX(0)' : 'translateX(60px)',
              transition: 'opacity 900ms cubic-bezier(0.22,1,0.36,1) 150ms, transform 900ms cubic-bezier(0.22,1,0.36,1) 150ms',
            }}
          >
            <div className="philosophy-image-wrap" style={{
              position: 'relative',
              display: isMobile ? 'flex' : 'block',
              flexDirection: isMobile ? 'column' : undefined,
              width: '100%',
              height: isMobile ? 'auto' : '100%',
            }}>
              {/* Offset gold frame — desktop only */}
              {!isMobile && (
                <div className="philosophy-frame" style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  right: -8,
                  bottom: -8,
                  border: '1px solid #C9A96E',
                  pointerEvents: 'none',
                  zIndex: 0,
                }} />
              )}
              {/* Photo */}
              <div className="philosophy-photo-inner" style={{
                position: 'relative',
                zIndex: 1,
                overflow: 'hidden',
                height: isMobile ? 'auto' : '100%',
                borderRadius: isMobile ? 14 : 0,
                boxShadow: isMobile ? '0 8px 32px rgba(20,18,14,0.14)' : 'none',
              }}>
                <img
                  ref={philosophyImgRef}
                  src={philosophyImageUrl}
                  alt="NIVORA Studio — editorial"
                  className="philosophy-photo"
                  style={{
                    width: '100%',
                    height: isMobile ? 'auto' : '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                  loading="lazy"
                />
              </div>

              {/* Glass quote card — overlaps image on desktop, flows below on mobile */}
              <div
                ref={quoteCardRef}
                className="philosophy-quote-card"
                data-inview={philosophyInView ? 'true' : 'false'}
                style={isMobile ? {
                  position: 'static',
                  marginTop: 16,
                  width: '100%',
                  backgroundColor: 'rgba(10,14,8,0.88)',
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)',
                  border: '1.5px solid #C9A96E',
                  outline: '1px solid rgba(201,169,110,0.45)',
                  outlineOffset: '6px',
                  padding: '16px 18px',
                  boxShadow: '0 18px 40px rgba(0,0,0,0.28)',
                  opacity: philosophyInView ? 1 : 0,
                  transform: philosophyInView ? 'translateY(0)' : 'translateY(14px)',
                  transition: 'opacity 500ms ease-out 300ms, transform 500ms ease-out 300ms',
                } : {
                  position: 'absolute',
                  left: -32,
                  bottom: -32,
                  zIndex: 2,
                  width: '45%',
                  maxWidth: '45%',
                  backgroundColor: 'rgba(0,0,0,0.45)',
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)',
                  border: '1.5px solid #C9A96E',
                  padding: '16px',
                  boxShadow: '0 18px 40px rgba(0,0,0,0.28)',
                  opacity: philosophyInView ? 1 : 0,
                  transform: philosophyInView ? 'scale(1)' : 'scale(0.4)',
                  transition: 'opacity 500ms ease-out 950ms, transform 650ms cubic-bezier(0.34, 1.56, 0.64, 1) 950ms',
                }}
              >
                <div style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontStyle: 'italic',
                  fontSize: 28,
                  lineHeight: 0.6,
                  color: '#C9A96E',
                  marginBottom: 14,
                  opacity: 0.9,
                }} className="philosophy-quote-mark">
                  "
                </div>
                <p style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontStyle: 'italic',
                  fontWeight: 400,
                  fontSize: 15,
                  lineHeight: 1.5,
                  color: '#ffffff',
                  margin: 0,
                }} className="philosophy-quote-text">
                  We don't just design spaces, we create legacies.
                </p>
                <p style={{
                  fontFamily: "'Jost', sans-serif",
                  fontWeight: 400,
                  fontSize: 12,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: '#C9A96E',
                  marginTop: 12,
                  marginBottom: 0,
                }} className="philosophy-quote-author">
                  — Shweta, Founder
                </p>
              </div>
            </div>
          </div>

        </div>

        <style>{`
          @media (max-width: 767px) {

            /* ── Section horizontal padding ──────────────────────────── */
            .philosophy-section {
              padding-left: 20px !important;
              padding-right: 20px !important;
              box-sizing: border-box !important;
              overflow: hidden !important;
            }

            /* ── Layout: column, text first then image ───────────────── */
            .philosophy-flex {
              flex-direction: column !important;
              gap: 0 !important;
            }
            .philosophy-text-block {
              flex: none !important;
              min-width: 0 !important;
              width: 100% !important;
              box-sizing: border-box !important;
              order: 1 !important;
              margin-bottom: 28px !important;
            }
            .philosophy-image-col {
              flex: none !important;
              min-width: 0 !important;
              width: 100% !important;
              height: auto !important;
              order: 2 !important;
              align-self: stretch !important;
              margin: 0 !important;
            }

            /* ── Heading — responsive clamp, comfortable line-height ─── */
            .philosophy-text-block > p:first-of-type {
              font-size: clamp(1.4rem, 5.5vw, 1.8rem) !important;
              line-height: 1.45 !important;
            }

            /* ── Label flanking rules — shorter on narrow screens ─────── */
            .philosophy-label-row {
              gap: 10px !important;
            }
            .philosophy-label-rule {
              width: 36px !important;
              flex-shrink: 0 !important;
            }

            /* ── Image wrap — column stack, image on top, card below ─── */
            .philosophy-image-wrap {
              display: flex !important;
              flex-direction: column !important;
              align-items: stretch !important;
              position: relative !important;
              width: 100% !important;
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
              border-radius: 0 !important;
              margin: 0 !important;
            }
            .philosophy-frame {
              display: none !important;
            }

            /* ── Photo inner — full width, natural height ────────────── */
            .philosophy-photo-inner {
              width: 100% !important;
              flex-shrink: 1 !important;
              height: auto !important;
              min-height: 0 !important;
              overflow: hidden !important;
              border-radius: 14px !important;
              position: relative !important;
              box-shadow: 0 8px 32px rgba(20,18,14,0.14) !important;
            }

            /* ── Photo — static flow so photo-inner gets natural height ─ */
            .philosophy-photo-inner .philosophy-photo {
              position: static !important;
              inset: auto !important;
              width: 100% !important;
              height: auto !important;
              max-width: 100% !important;
              object-fit: unset !important;
              object-position: unset !important;
              display: block !important;
              transform: none !important;
            }

            /* ── Quote card — full-width block below image ───────────── */
            .philosophy-quote-card {
              position: static !important;
              top: auto !important; left: auto !important;
              right: auto !important; bottom: auto !important;
              width: 100% !important;
              flex-shrink: 1 !important;
              max-width: 100% !important;
              margin: 16px 0 0 0 !important;
              background-color: rgba(10,14,8,0.88) !important;
              background: rgba(10,14,8,0.88) !important;
              backdrop-filter: blur(6px) !important;
              -webkit-backdrop-filter: blur(6px) !important;
              border: 1.5px solid #C9A96E !important;
              outline: 1px solid rgba(201,169,110,0.45) !important;
              outline-offset: 6px !important;
              border-radius: 0px !important;
              padding: 16px 18px !important;
              box-shadow: 0 18px 40px rgba(0,0,0,0.28) !important;
              display: block !important;
              justify-content: unset !important;
              z-index: auto !important;
              transform: translateY(14px) !important;
              opacity: 0 !important;
              transition: opacity 500ms ease-out 300ms,
                          transform 500ms ease-out 300ms !important;
            }
            .philosophy-in-view .philosophy-quote-card {
              transform: translateY(0) !important;
              opacity: 1 !important;
            }

            /* ── Quote card typography ───────────────────────────────── */
            .philosophy-quote-mark {
              font-size: 22px !important;
              line-height: 0.8 !important;
              margin-bottom: 10px !important;
            }
            .philosophy-quote-text {
              font-size: 12.5px !important;
              line-height: 1.55 !important;
              margin: 0 !important;
            }
            .philosophy-quote-author {
              font-size: 10px !important;
              letter-spacing: 1.5px !important;
              margin-top: 10px !important;
              margin-bottom: 0 !important;
            }

            /* ── Text block entrance animations (slide up) ───────────── */
            .philosophy-mobile-fade {
              transform: translateY(20px) !important;
            }
            .philosophy-in-view .philosophy-mobile-fade {
              transform: translateY(0) !important;
            }
          }
        `}</style>
      </section>

      {/* Stats strip — desktop grid */}
      <StatsSection />

      {/* Stats carousel — mobile only (hidden via CSS above 768px) */}
      <MobileStatsCarousel />

      {/* Services */}
      <section className="services-section-home" style={{ backgroundColor: '#F7F4EF', padding: '7rem 1.5rem' }}>
        <style>{`
          /* ── Service card shell ── */
          .hsvc-card {
            position: relative;
            border-radius: 24px;
            overflow: hidden;
            height: 380px;
            border: 1px solid #E8DED1;
            text-decoration: none;
            display: block;
            cursor: pointer;
            background: #F3EEE7;
            transition: transform 500ms cubic-bezier(0.22,1,0.36,1),
                        box-shadow 500ms cubic-bezier(0.22,1,0.36,1);
          }
          .hsvc-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 28px 64px rgba(38,36,33,0.13), 0 4px 16px rgba(38,36,33,0.07);
          }

          /* Image zoom */
          .hsvc-img {
            transition: transform 700ms cubic-bezier(0.22,1,0.36,1);
          }
          .hsvc-card:hover .hsvc-img { transform: scale(1.08); }

          /* Gradient overlay */
          .hsvc-overlay {
            position: absolute; inset: 0; pointer-events: none;
            background: linear-gradient(
              to bottom,
              rgba(15,12,10,0.16) 0%,
              rgba(10,8,6,0.60) 52%,
              rgba(6,5,4,0.92) 100%
            );
            transition: background 500ms ease;
          }
          .hsvc-card:hover .hsvc-overlay {
            background: linear-gradient(
              to bottom,
              rgba(15,12,10,0.30) 0%,
              rgba(10,8,6,0.80) 42%,
              rgba(6,5,4,0.97) 100%
            );
          }

          /* ── TOP LAYER: number badge ── */
          .hsvc-num {
            position: absolute; top: 1.5rem; left: 1.6rem; z-index: 4;
            font-family: 'Jost', sans-serif; font-weight: 300;
            font-size: 10px; letter-spacing: 0.3em;
            color: rgba(200,165,106,0.7); margin: 0;
          }

          /* ── MIDDLE LAYER: icon + title — always visible, never moves into detail area ── */
          .hsvc-mid-layer {
            position: absolute;
            left: 1.6rem; right: 1.6rem; bottom: 168px;
            z-index: 3;
            transition: transform 500ms cubic-bezier(0.22,1,0.36,1);
          }
          .hsvc-card:hover .hsvc-mid-layer { transform: translateY(-16px); }

          .hsvc-icon-wrap {
            width: 40px; height: 40px; border-radius: 10px;
            background: rgba(200,165,106,0.15);
            display: flex; align-items: center; justify-content: center;
            margin-bottom: 0.6rem;
            transition: background 400ms ease;
          }
          .hsvc-card:hover .hsvc-icon-wrap { background: rgba(200,165,106,0.28); }

          .hsvc-title {
            font-family: 'Cormorant Garamond', serif;
            font-weight: 300;
            font-size: clamp(1.25rem, 1.8vw, 1.55rem);
            color: #f5f0e8;
            line-height: 1.2;
            margin: 0;
            letter-spacing: -0.005em;
          }

          /* ── BOTTOM LAYER: gold line + desc + explore — hidden at rest ── */
          /* Occupies bottom 25px → ~165px. Never overlaps mid-layer (bottom: 168px). */
          .hsvc-detail-layer {
            position: absolute;
            left: 1.6rem; right: 1.6rem; bottom: 1.5rem;
            z-index: 3;
            opacity: 0;
            transform: translateY(18px);
            pointer-events: none;
            transition: opacity 420ms cubic-bezier(0.22,1,0.36,1) 55ms,
                        transform 420ms cubic-bezier(0.22,1,0.36,1) 55ms;
          }
          .hsvc-card:hover .hsvc-detail-layer {
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
          }

          /* Gold accent line */
          .hsvc-gold-line {
            width: 0; height: 1.5px;
            background: #C8A56A; border-radius: 2px;
            transition: width 500ms cubic-bezier(0.22,1,0.36,1);
            margin: 0 0 0.75rem;
          }
          .hsvc-card:hover .hsvc-gold-line { width: 36px; }

          /* Description — capped at 3 lines to guarantee no overflow */
          .hsvc-desc {
            font-family: 'Jost', sans-serif; font-weight: 300;
            font-size: 12.5px; color: rgba(245,240,232,0.78);
            line-height: 1.72; margin: 0;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          /* Explore CTA */
          .hsvc-explore {
            display: inline-flex; align-items: center; gap: 6px;
            margin-top: 0.85rem;
            font-family: 'Jost', sans-serif; font-weight: 300;
            font-size: 10px; letter-spacing: 0.22em;
            text-transform: uppercase; color: #C8A56A;
          }

          /* Grids */
          .hsvc-grid-r1 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
          .hsvc-grid-r2 {
            display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px;
            margin: 20px auto 0; max-width: 895px;
          }

          @media (max-width: 1100px) {
            .hsvc-grid-r1 { grid-template-columns: repeat(2, 1fr) !important; }
            .hsvc-grid-r2 { grid-template-columns: repeat(2, 1fr) !important; max-width: 100% !important; }
            .hsvc-card { height: 360px !important; }
            .hsvc-mid-layer { bottom: 160px !important; }
          }
          @media (max-width: 767px) {
            .hsvc-grid-r1, .hsvc-grid-r2 { display: none !important; }
          }
        `}</style>

        {/* Header — desktop only */}
        <div className="hsvc-desktop-header">
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: '4.5rem', maxWidth: 640, margin: '0 auto 4.5rem' }}>
              <p style={{
                fontFamily: "'Jost', sans-serif",
                fontWeight: 300,
                fontSize: 10,
                letterSpacing: '0.45em',
                textTransform: 'uppercase',
                color: '#C8A56A',
                marginBottom: '1rem',
              }}>Our Services</p>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 400,
                fontSize: 'clamp(2rem, 4vw, 3.25rem)',
                color: '#262421',
                lineHeight: 1.1,
                marginBottom: '1.1rem',
                letterSpacing: '-0.01em',
              }}>Spaces Designed Across<br />Every Experience</h2>
              <p className="svc-header-desc" style={{
                fontFamily: "'Jost', sans-serif",
                fontWeight: 300,
                fontSize: 14,
                color: 'rgba(38,36,33,0.5)',
                lineHeight: 1.85,
                margin: 0,
              }}>
                From private residences to large-scale environments, we design spaces that balance beauty, function and identity.
              </p>
            </div>
          </FadeIn>
        </div>

        {/* Cards — desktop only */}
        <div className="hsvc-desktop-cards" style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* Row 1 — 4 cards */}
          <div className="hsvc-grid-r1">
            {services.slice(0, 4).map((s, i) => {
              const Icon = s.icon
              return (
                <FadeIn key={s.title} delay={i * 0.09}>
                  <Link to="/services" className="hsvc-card">
                    {/* Image */}
                    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 24 }}>
                      <img src={s.img} alt={s.title} className="hsvc-img"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        loading="lazy" />
                    </div>
                    {/* Overlay */}
                    <div className="hsvc-overlay" />
                    {/* Number — top-left, independent layer */}
                    <p className="hsvc-num">{s.num}</p>
                    {/* Mid layer: icon + title — always visible, fixed position */}
                    <div className="hsvc-mid-layer">
                      <div className="hsvc-icon-wrap">
                        <Icon size={18} color="#C8A56A" strokeWidth={1.4} />
                      </div>
                      <h3 className="hsvc-title">{s.title}</h3>
                    </div>
                    {/* Detail layer: fades in on hover, positioned below mid-layer */}
                    <div className="hsvc-detail-layer">
                      <div className="hsvc-gold-line" />
                      <p className="hsvc-desc">{s.desc}</p>
                      <span className="hsvc-explore">
                        Explore <ArrowRight size={10} strokeWidth={1.5} />
                      </span>
                    </div>
                  </Link>
                </FadeIn>
              )
            })}
          </div>
        </div>

        {/* Mobile layout — shown only below 768px */}
        <MobileServicesSection />
      </section>

      {/* Process — Light Editorial Rebuild */}


      {/* Before / After — Transformation Carousel */}
      <section style={{ backgroundColor: '#FAF8F4', padding: '8px 1.5rem' }}>
        <style>{`
          .compare-slider-container {
            aspect-ratio: 16 / 10;
          }
          @media (max-width: 640px) {
            .compare-slider-container {
              aspect-ratio: 16 / 10;
            }
          }
        `}</style>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* Header */}
          <FadeIn>
            <style>{`
              .trf-header-row {
                position: relative;
                display: flex;
                align-items: flex-start;
                justify-content: center;
                margin-bottom: 4rem;
              }
              .trf-header-left {
                text-align: center;
              }
              .trf-header-right {
                position: absolute;
                top: 0;
                right: 0;
                width: 320px;
                padding-top: 0.35rem;
                text-align: right;
              }
              @media (max-width: 700px) {
                .trf-header-row { flex-direction: column; align-items: center; }
                .trf-header-left { text-align: center; }
                .trf-header-right { position: static; width: auto; text-align: center; padding-top: 0; }
              }
            `}</style>
            <div className="trf-header-row">
              <div className="trf-header-left">
                <p style={{
                  fontFamily: "'Jost', sans-serif", fontWeight: 300, fontSize: 10,
                  letterSpacing: '0.45em', textTransform: 'uppercase',
                  color: '#C8A56A', marginBottom: '1rem',
                }}>Transformations</p>
                <h2 style={{
                  fontFamily: "'Playfair Display', serif", fontWeight: 400,
                  fontSize: 'clamp(2rem, 4vw, 3.25rem)',
                  color: '#262421', lineHeight: 1.1, marginBottom: '1rem',
                  letterSpacing: '-0.01em',
                }}>Before &amp; After</h2>
                <p style={{
                  fontFamily: "'Jost', sans-serif", fontWeight: 300, fontSize: 14,
                  color: 'rgba(38,36,33,0.5)', lineHeight: 1.85, margin: 0,
                }}>
                  See how thoughtful design transforms spaces into refined living experiences.
                </p>
              </div>
              <div className="trf-header-right">
                <p style={{
                  fontFamily: "'Montserrat', sans-serif", fontWeight: 400,
                  fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: 'rgba(38,36,33,0.38)', lineHeight: 1.9, margin: 0,
                }}>
                  Slide to discover how we transform raw spaces into refined living experiences.
                </p>
              </div>
            </div>
          </FadeIn>

          {/* Carousel */}
          <FadeIn delay={0.18}>
            <TransformationCarousel />
          </FadeIn>
        </div>
      </section>

      {/* Our Expertise — 4 Category Cards */}
      <section style={{ backgroundColor: '#F8F6F2', padding: '72px 0 80px' }}>
        <style>{`
          .oe-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 24px;
            max-width: 1280px;
            margin: 0 auto;
            padding: 0 2rem;
          }
          .oe-card {
            background: #fff;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 2px 16px rgba(20,18,14,0.06);
            transition: transform 0.45s cubic-bezier(0.16,1,0.3,1), box-shadow 0.45s ease;
            display: flex;
            flex-direction: column;
          }
          .oe-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 16px 48px rgba(20,18,14,0.13);
          }
          .oe-img-wrap {
            width: 100%;
            height: 240px;
            overflow: hidden;
            flex-shrink: 0;
          }
          .oe-img {
            width: 100%; height: 100%;
            object-fit: cover; display: block;
            transition: transform 0.7s cubic-bezier(0.16,1,0.3,1);
          }
          .oe-card:hover .oe-img { transform: scale(1.06); }
          .oe-body {
            padding: 24px 24px 28px;
            display: flex;
            flex-direction: column;
            flex: 1;
          }
          .oe-divider {
            width: 28px; height: 1px;
            background: #C9A96E;
            margin: 0 0 14px;
            display: block;
          }
          .oe-title {
            font-family: 'Playfair Display', serif;
            font-weight: 400;
            font-size: 1.18rem;
            color: #1a1612;
            line-height: 1.2;
            margin: 0 0 10px;
            letter-spacing: -0.01em;
          }
          .oe-desc {
            font-family: 'Jost', sans-serif;
            font-weight: 300;
            font-size: 13px;
            color: rgba(26,22,18,0.50);
            line-height: 1.75;
            margin: 0;
            flex: 1;
          }
          @media (max-width: 1023px) {
            .oe-grid { grid-template-columns: repeat(2, 1fr); gap: 20px; padding: 0 1.5rem; }
          }
          @media (max-width: 767px) {
            .oe-grid { display: none !important; }
            .oe-carousel-wrap { display: block !important; }
            .oe-img-wrap { height: 220px; }
          }
          .oe-carousel-wrap { display: none; }
        `}</style>

        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 2rem' }}>
          {/* Section header */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            style={{ textAlign: 'center', marginBottom: '48px' }}
          >
            <p style={{
              fontFamily: "'Jost', sans-serif", fontWeight: 300, fontSize: 10,
              letterSpacing: '0.46em', textTransform: 'uppercase',
              color: '#9B7D4E', marginBottom: '14px',
            }}>Our Expertise</p>
            <h2 style={{
              fontFamily: "'Playfair Display', serif", fontWeight: 400,
              fontSize: 'clamp(1.6rem, 2.8vw, 2.6rem)',
              color: '#1a1612', lineHeight: 1.1,
              margin: '0 0 18px', letterSpacing: '-0.015em',
            }}>Spaces Designed for<br />Every Lifestyle</h2>
            <div style={{
              width: 44, height: 1,
              background: 'linear-gradient(90deg, transparent, #C9A96E 40%, transparent)',
              margin: '0 auto 16px',
            }} />
            <p style={{
              fontFamily: "'Jost', sans-serif", fontWeight: 300, fontSize: 14,
              color: 'rgba(26,22,18,0.44)', lineHeight: 1.7,
              maxWidth: 420, margin: '0 auto',
            }}>
              Thoughtfully crafted interiors that blend beauty, functionality, and timeless elegance.
            </p>
          </motion.div>
        </div>

        {/* 4-card grid — desktop (≥1024px) and tablet (768–1023px) */}
        <div className="oe-grid">
          {serviceCards.map((card, i) => (
            <motion.div
              key={card.title}
              className="oe-card"
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: i * 0.1 }}
            >
              <div className="oe-img-wrap">
                <img src={card.img} alt={card.title} className="oe-img" loading="lazy" draggable={false} />
              </div>
              <div className="oe-body">
                <span className="oe-divider" />
                <h3 className="oe-title">{card.title}</h3>
                <p className="oe-desc">{card.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Autoplay carousel — mobile only (<768px) */}
        <div className="oe-carousel-wrap">
          <ExpertiseCarousel cards={serviceCards} />
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsCarousel />

      {/* Instagram */}
      <section style={{ background: '#FAF8F4', padding: '5rem 0' }}>
        <InstagramFeed posts={instagramPosts} />
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#33452F', paddingTop: 70, paddingBottom: 70 }}>
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #b8966a 0%, transparent 60%)' }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <FadeIn>
            <div style={{ width: 80, height: 1, backgroundColor: '#C9A96E', margin: '0 auto 24px' }} />
            <p className="text-[#b8966a] text-[10px] tracking-[0.4em] uppercase mb-6">Currently Accepting Projects</p>
            <h2 className="font-serif text-4xl md:text-6xl text-[#f5f0e8] font-light leading-tight mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
              Ready to Transform<br />Your Space?
            </h2>
            <p className="text-[#f5f0e8]/50 font-light mb-10 max-w-lg mx-auto leading-relaxed">
              A complimentary consultation. No pressure, just possibilities.
            </p>
            <Link
              to="/contact"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                background: 'linear-gradient(135deg, #E0C38A 0%, #C8A46A 50%, #A8854F 100%)',
                color: '#2D3E29',
                fontFamily: "'Cinzel', serif",
                fontWeight: 600,
                fontSize: 10,
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                padding: '20px 52px',
                textDecoration: 'none',
                transition: 'background 0.3s ease, box-shadow 0.3s ease',
                boxShadow: '0 6px 30px rgba(168,133,79,0.40)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #EDD09A 0%, #D4B078 50%, #B8904E 100%)'
                e.currentTarget.style.boxShadow = '0 10px 40px rgba(168,133,79,0.55)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #E0C38A 0%, #C8A46A 50%, #A8854F 100%)'
                e.currentTarget.style.boxShadow = '0 6px 30px rgba(168,133,79,0.40)'
              }}
            >
              Begin Your Project <ArrowRight size={14} />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
